import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider, useNodesState, useEdgesState, addEdge, Controls, Background, BackgroundVariant, MiniMap, Handle, Position, MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Zap, Send, Clock, GitBranch, Tag as TagIcon, Kanban, Plus, X, Trash2,
  History, ArrowLeft, Workflow as WorkflowIcon, Lock, FileText as FileTextIcon, Save, Pencil, Bot, BellRing,
  ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Braces, Paperclip, Thermometer,
  Shuffle, Sun, UserCog, Flag, Sparkles, UserCheck, PhoneOutgoing
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatEcuadorDate, formatEcuadorTime } from '../../utils/ecuadorTime';
import { CUSTOM_VALUES, insertAtCursor } from '../../utils/customValues';
import { ATTACHMENT_ACCEPT, buildAttachmentFromFile } from '../../utils/mediaAttachment';
import CustomAlert from './shared/CustomAlert';

// ⭐ Módulo Workflows/Automatizaciones (2026-08-05, pedido de Angelo: "que
// se vea, se sienta y sepa como a GHL... vamos a por las ligas mayores").
// Constructor visual de nodos con React Flow (única librería nueva que se
// suma en esta fase -- ver evaluación de riesgo en el commit: el resto del
// proyecto no usa ninguna librería de canvas/diagramas, y hacer el motor
// de arrastrar-y-conectar nodos a mano hubiera sido mucho más código y
// mucho más riesgo de bugs sutiles que adoptar una librería madura y
// ampliamente usada para exactamente este problema).
//
// Ver backend/crm/workflowEngine.js para el motor de ejecución real
// (triggers, pasos, anti-loop) -- este archivo SOLO arma/edita/guarda el
// grafo visual y llama a los endpoints CRUD de index.js.

const TRIGGER_LABELS = {
  // ⭐ Encadenar workflows (2026-08-09). Un workflow con ESTE disparador no
  // arranca solo: solo corre cuando otro lo llama con el paso "Llamar a
  // workflow". Sirve para armar sub-flujos reutilizables (ej. "Secuencia de
  // nutrición") y llamarlos desde varios flujos distintos sin duplicarlos.
  workflow_called: 'Llamado desde otro workflow',
  stage_changed: 'Contacto cambia de etapa',
  tag_added: 'Se agrega una etiqueta',
  contact_created: 'Nuevo contacto',
  message_received: 'Cliente responde por WhatsApp',
  field_changed: 'Cambia un campo del contacto',
  // ⭐ Pedido de Angelo, 2026-08-07: "trigger por fecha de cumpleaños". Sin
  // configuración adicional (comodín, como contact_created/message_received)
  // -- revisa `crm_contacts.birthday` una vez al día vía cron, ver
  // backend/index.js.
  birthday: 'Cumpleaños del contacto',
  // ⭐ Calendarios (pedido de Angelo, 2026-08-07): se dispara cuando alguien
  // reserva una cita desde la página pública (/reservar/:slug). Mismo
  // comodín que contact_created/message_received/birthday -- sin filtro
  // por calendario específico en esta primera versión, ver
  // dispatchTrigger('appointment_booked', ...) en backend/index.js.
  appointment_booked: 'Se reserva una cita (Calendarios)',
};

// ⭐ Workflows v2 (2026-08-09, "rediseño irreconocible" pedido de Angelo) --
// cada tipo de paso trae ahora un `color` (una de las claves de
// STEP_COLOR_CLASSES, ver abajo) usado para su badge en el canvas Y en la
// barra "Agregar paso", así los nodos de la misma familia (mensajería,
// pipeline, lógica, IA) se distinguen a simple vista sin leer el texto.
// 'condition' se agrega acá también (antes tenía su propio botón
// hardcodeado aparte) para que la barra de "Agregar paso" se pueda armar
// recorriendo un solo objeto, agrupado por categoría (ver STEP_CATEGORIES).
const STEP_META = {
  send_message: { label: 'Enviar mensaje', icon: Send, color: 'blue' },
  wait: { label: 'Esperar', icon: Clock, color: 'gray' },
  add_tag: { label: 'Agregar etiqueta', icon: TagIcon, color: 'green' },
  remove_tag: { label: 'Quitar etiqueta', icon: TagIcon, color: 'rose' },
  set_stage: { label: 'Cambiar de etapa', icon: Kanban, color: 'blue' },
  // ⭐ Nuevo (pedido de Angelo, 2026-08-08: "tenemos temperatura de lead
  // pero nada que la automatice") -- mismo patrón que set_stage, ver
  // executeStep en workflowEngine.js.
  set_temperature: { label: 'Cambiar temperatura', icon: Thermometer, color: 'amber' },
  // ⭐ Nodo de IA (pedido de Angelo, 2026-08-06) -- solo aparece en la barra
  // de "Agregar paso" si la cuenta tiene crm_ai_enabled, ver hasAiAccess
  // más abajo. El STEP_META queda igual para todos así el nodo se sigue
  // renderizando bien (ícono/label) incluso si luego se desactiva la IA
  // con el workflow ya guardado.
  ai_reply: { label: 'Responder con IA', icon: Bot, color: 'purple' },
  // ⭐ Notificación interna (pedido de Angelo, 2026-08-07, inspirado en
  // "Internal Notification" de GHL): a diferencia de send_message, esto le
  // avisa al NEGOCIO (dueño/empleados/números sueltos), no al contacto --
  // ver executeInternalNotification en workflowEngine.js.
  internal_notification: { label: 'Notificación interna (WhatsApp)', icon: BellRing, color: 'amber' },
  // ⭐ Llamar a otro workflow (2026-08-09, pedido de Angelo). El destino debe
  // tener el disparador "Llamado desde otro workflow". Es fire-and-forget: se
  // crea la corrida del sub-flujo y este sigue con su próximo paso.
  call_workflow: { label: 'Llamar a workflow', icon: PhoneOutgoing, color: 'purple' },
  condition: { label: 'Condición', icon: GitBranch, color: 'amber' },
  // ⭐ Workflows v2 (2026-08-09, pedido de Angelo: "ramas customizables
  // estilo GHL" + "total control creativo para agregar nodos"). Cada uno se
  // explica también en su propio panel de configuración (ver más abajo) --
  // acá solo el resumen para el botón de "Agregar paso" y el header del
  // panel:
  //   'branch'          Reemplazo GENERALIZADO de 'condition': en vez de
  //                      Sí/No fijo, tantas ramas como el usuario quiera,
  //                      cada una con su propio nombre y sus propias
  //                      condiciones (o un split A/B por porcentaje). Usar
  //                      en vez de encadenar varias 'Condición' seguidas
  //                      cuando hay más de 2 caminos posibles (ej. "interesado
  //                      / no interesado / ya es cliente" en un solo nodo).
  //   'business_hours'  2 salidas fijas (Dentro/Fuera de horario). Usar
  //                      antes de un 'Responder con IA' o 'Enviar mensaje'
  //                      para no escribirle a un cliente a las 3am, o para
  //                      derivar a "Notificación interna" solo en horario
  //                      de atención.
  //   'update_field'    Escribe un dato del contacto (nombre, cédula,
  //                      sector, cumpleaños) sin depender de que el cliente
  //                      lo escriba en el chat -- útil después de un
  //                      'Detectar intención (IA)' o una 'Condición' que ya
  //                      dedujo el dato.
  //   'end_goal'        Nodo terminal con nombre propio (ej. "Agendó cita",
  //                      "Se dio de baja"). Usar al final de cada camino del
  //                      flujo para que el Historial muestre EN QUÉ terminó
  //                      cada contacto, no solo "completado" a secas.
  //   'ai_intent'       1 sola llamada a IA que clasifica el último mensaje
  //                      del contacto contra una lista de intenciones que
  //                      vos definís (ej. "quiere comprar", "quiere
  //                      soporte", "quiere cancelar") y rama según cuál
  //                      matcheó. Usar en vez de una 'Condición' por
  //                      palabra clave cuando el cliente puede escribir de
  //                      muchas formas distintas la misma intención.
  branch: { label: 'Rama múltiple', icon: Shuffle, color: 'indigo' },
  business_hours: { label: 'Horario laboral', icon: Sun, color: 'teal' },
  update_field: { label: 'Actualizar campo', icon: UserCog, color: 'blue' },
  end_goal: { label: 'Fin / Meta', icon: Flag, color: 'green' },
  ai_intent: { label: 'Detectar intención (IA)', icon: Sparkles, color: 'purple' },
  ai_extract: { label: 'Extraer datos con IA', icon: Sparkles, color: 'purple' },
};

// ⭐ Clases Tailwind fijas por color (NUNCA se interpolan dinámicamente
// `bg-${color}-100` en el JSX -- Tailwind no puede purgar/generar clases
// armadas en runtime, así que cada color vive acá como string completo).
const STEP_COLOR_CLASSES = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', hex: '#2563eb' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', hex: '#d97706' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-600', hex: '#e11d48' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600', hex: '#0d9488' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', hex: '#9333ea' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', hex: '#4f46e5' },
  green: { bg: 'bg-green-100', text: 'text-green-600', hex: '#16a34a' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600', hex: '#6b7280' },
};

// ⭐ Agrupa STEP_META por familia para la barra "Agregar paso" -- puramente
// de presentación (orden y agrupación visual), no afecta guardado/ejecución.
const STEP_CATEGORIES = [
  { label: 'Mensajería', types: ['send_message', 'internal_notification', 'ai_reply'] },
  { label: 'Contacto y pipeline', types: ['add_tag', 'remove_tag', 'set_stage', 'set_temperature', 'update_field'] },
  { label: 'Lógica y tiempo', types: ['wait', 'condition', 'branch', 'business_hours', 'ai_intent', 'ai_extract'] },
  { label: 'Cierre', types: ['end_goal', 'call_workflow'] },
];

// ⭐ El motor de IA es genérico desde el 2026-08-06 (ver callChatCompletion
// en backend/crm/aiReply.js): habla el formato OpenAI-compatible, así que
// CUALQUIER proveedor configurado en la cuenta (api key + URL + modelo,
// editable desde el panel de administración) funciona igual, sin volver a
// tocar este archivo. Ya no hay una lista de proveedores que elegir acá.

const TEMP_LABELS = { frio: 'Frío', tibio: 'Tibio', caliente: 'Caliente' };

// ⭐ Vocabulario de tipos de mensaje para la condición multi-rama (pedido de
// Angelo, 2026-08-07: "que reconozca si el tipo de mensaje es video, texto,
// audio... que no necesariamente deban ir las 3, sino que pueda ir una,
// otra, o las 3, así mismo sticker, gif o fotos"). Espejo del `type` real
// que guarda crm_messages (ver extractMediaInfo en baileysManager.js) --
// 'gif' es un caso especial: WhatsApp no tiene un mensaje nativo de GIF, es
// un video con gifPlayback=true que el backend ya distingue como 'gif'.
const MESSAGE_TYPE_BRANCH_META = [
  { key: 'text', label: 'Texto', emoji: '💬' },
  { key: 'image', label: 'Foto', emoji: '📷' },
  { key: 'video', label: 'Video', emoji: '🎬' },
  { key: 'gif', label: 'GIF', emoji: '🎞️' },
  { key: 'audio', label: 'Audio', emoji: '🎤' },
  { key: 'sticker', label: 'Sticker', emoji: '😄' },
  { key: 'document', label: 'Documento', emoji: '📄' },
];

const RUN_STATUS = {
  running: { label: 'Corriendo', className: 'bg-blue-100 text-blue-700' },
  waiting: { label: 'En espera', className: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completado', className: 'bg-green-100 text-green-700' },
  failed: { label: 'Falló', className: 'bg-red-100 text-red-700' },
};

function safeParse(text) {
  if (!text) return {};
  if (typeof text === 'object') return text;
  try { const p = JSON.parse(text); return p && typeof p === 'object' ? p : {}; } catch (_) { return {}; }
}

function describeTrigger(triggerType, config, { pipelineStages, tags }) {
  if (triggerType === 'stage_changed') {
    if (!config.stageId) return 'Cuando pasa a cualquier etapa';
    const stage = pipelineStages.find(s => String(s.id) === String(config.stageId));
    return `Cuando pasa a "${stage?.name || '...'}"`;
  }
  if (triggerType === 'tag_added') {
    if (!config.tagId) return 'Con cualquier etiqueta nueva';
    const tag = tags.find(t => String(t.id) === String(config.tagId));
    return `Con la etiqueta "${tag?.name || '...'}"`;
  }
  if (triggerType === 'contact_created') return 'Cada vez que llega un contacto nuevo';
  if (triggerType === 'message_received') return 'Cada vez que un cliente responde';
  if (triggerType === 'field_changed') return config.field ? `Cuando cambia ${config.field}` : 'Cuando cambia un campo';
  if (triggerType === 'birthday') return 'El día del cumpleaños del contacto';
  if (triggerType === 'appointment_booked') return 'Cada vez que alguien reserva una cita';
  return 'Sin configurar';
}

function describeStep(stepType, config, { pipelineStages, tags, templates, aiContexts }) {
  switch (stepType) {
    case 'send_message': {
      if (config.templateId) {
        const tpl = templates.find(t => String(t.id) === String(config.templateId));
        return `Plantilla: ${tpl?.name || '(borrada)'}`;
      }
      if (config.content) return `"${config.content.slice(0, 40)}${config.content.length > 40 ? '…' : ''}"`;
      return 'Sin mensaje';
    }
    case 'wait': {
      // ⭐ Modo "hasta que responda" (pedido de Angelo, 2026-08-07, calcado
      // del action Wait de GHL) -- ver case 'wait' en workflowEngine.js.
      if (config.mode === 'until_reply') {
        if (config.timeoutEnabled === false) return 'Esperar respuesta por WhatsApp (sin límite)';
        const val = config.timeoutValue ?? 30;
        const unit = config.timeoutUnit === 'hours' ? 'h' : config.timeoutUnit === 'days' ? 'd' : 'min';
        return `Esperar respuesta por WhatsApp (máx ${val}${unit})`;
      }
      const min = Number(config.minutes) || 0;
      if (!min) return 'Sin configurar';
      if (min >= 1440) return `Esperar ${Math.round(min / 1440)} día(s)`;
      if (min >= 60) return `Esperar ${Math.round(min / 60)} hora(s)`;
      return `Esperar ${min} min`;
    }
    case 'add_tag':
    case 'remove_tag': {
      const tag = tags.find(t => String(t.id) === String(config.tagId));
      return tag ? tag.name : 'Sin etiqueta';
    }
    case 'set_stage': {
      if (config.stageId === undefined) return 'Sin configurar';
      if (!config.stageId) return 'Volver a "Sin etapa"';
      const stage = pipelineStages.find(s => String(s.id) === String(config.stageId));
      return stage?.name || '...';
    }
    case 'set_temperature': {
      if (!config.temperature) return 'Sin configurar';
      return TEMP_LABELS[config.temperature] || config.temperature;
    }
    case 'condition': {
      if (!config.field) return 'Sin configurar';
      if (config.field === 'message') {
        if (!config.value) return 'Mensaje: sin configurar';
        const opLabels = { contains: 'contiene', not_contains: 'no contiene', equals: 'es exactamente', starts_with: 'empieza con' };
        return `Mensaje ${opLabels[config.matchType] || 'contiene'} "${config.value}"`;
      }
      if (config.field === 'message_type') {
        const branches = Array.isArray(config.branches) ? config.branches : [];
        if (!branches.length) return 'Tipo de mensaje: sin ramas activas';
        const labels = branches.map(k => MESSAGE_TYPE_BRANCH_META.find(m => m.key === k)?.label || k);
        return `Tipo de mensaje: ${labels.join(' / ')}`;
      }
      const fieldLabels = { stage: 'Etapa', tag: 'Etiqueta', temperature: 'Temperatura' };
      let valueLabel = config.value;
      if (config.field === 'stage') valueLabel = pipelineStages.find(s => String(s.id) === String(config.value))?.name;
      if (config.field === 'tag') valueLabel = tags.find(t => String(t.id) === String(config.value))?.name;
      if (config.field === 'temperature') valueLabel = TEMP_LABELS[config.value];
      return `${fieldLabels[config.field]} = ${valueLabel || '...'}`;
    }
    case 'ai_reply': {
      // Con contexto guardado se muestra su NOMBRE: es lo que permite ver de un
      // vistazo, en el canvas, qué nodo usa cuál sin abrir ninguno.
      if (config.aiContextId) {
        const ctx = (aiContexts || []).find(c => String(c.id) === String(config.aiContextId));
        return `IA · contexto "${ctx ? ctx.name : '(borrado)'}"`;
      }
      if (!config.knowledgeBase) return 'IA de la cuenta · contexto general';
      return `IA de la cuenta · "${config.knowledgeBase.slice(0, 30)}${config.knowledgeBase.length > 30 ? '…' : ''}"`;
    }
    case 'internal_notification': {
      const fixedCount = String(config.fixedNumbers || '').split(',').map(s => s.trim()).filter(Boolean).length;
      const empCount = Array.isArray(config.employeeIds) ? config.employeeIds.length : 0;
      const total = fixedCount + empCount;
      if (!total) return 'Sin destinatarios configurados';
      // Se marca en el canvas que este aviso lo redacta la IA -- si no, un nodo
      // con IA y uno con texto fijo se ven exactamente igual.
      if (config.useAi) return `✨ Resumen con IA · a ${total} destinatario${total === 1 ? '' : 's'}`;
      const parts = [];
      if (fixedCount) parts.push(`${fixedCount} número${fixedCount === 1 ? '' : 's'} fijo${fixedCount === 1 ? '' : 's'}`);
      if (empCount) parts.push(`${empCount} empleado${empCount === 1 ? '' : 's'}`);
      return `A ${total} destinatario${total === 1 ? '' : 's'} (${parts.join(' + ')})`;
    }
    case 'branch': {
      const branches = Array.isArray(config.branches) ? config.branches.filter(b => b.key) : [];
      if (!branches.length) return 'Sin ramas configuradas';
      if (config.mode === 'random') return `Split A/B · ${branches.map(b => `${b.label || b.key} ${b.weight || 0}%`).join(' / ')}`;
      return `${branches.length} rama${branches.length === 1 ? '' : 's'}: ${branches.map(b => b.label || b.key).join(' / ')}`;
    }
    case 'business_hours': {
      const days = config.schedule && typeof config.schedule === 'object' ? Object.keys(config.schedule).filter(d => Array.isArray(config.schedule[d]) && config.schedule[d].length) : [];
      if (!days.length) return 'Sin horario configurado';
      return `${days.length} día${days.length === 1 ? '' : 's'} con horario definido`;
    }
    case 'update_field': {
      const FIELD_LABELS = { name: 'Nombre', email: 'Email', cedula: 'Cédula', sector: 'Sector', birthday: 'Cumpleaños' };
      if (!config.field) return 'Sin configurar';
      return `${FIELD_LABELS[config.field] || config.field} → "${(config.value || '').slice(0, 24)}${(config.value || '').length > 24 ? '…' : ''}"`;
    }
    case 'end_goal': {
      if (!config.goal) return 'Sin nombre configurado';
      return `Meta: "${config.goal}"`;
    }
    case 'call_workflow': {
      if (!config.workflowId) return 'Sin workflow elegido';
      return config.workflowName ? `Llama a "${config.workflowName}"` : `Llama al workflow #${config.workflowId}`;
    }
    case 'ai_intent': {
      const intents = Array.isArray(config.intents) ? config.intents.filter(i => i.key && i.name) : [];
      if (!intents.length) return 'Sin intenciones configuradas';
      return `${intents.length} intención${intents.length === 1 ? '' : 'es'}: ${intents.map(i => i.name).join(' / ')}`;
    }
    case 'ai_extract': {
      const targets = Array.isArray(config.targets) ? config.targets : [];
      return targets.length ? `Extrae ${targets.map(t => t.field).join(', ')}` : 'Sin campos configurados';
    }
    default:
      return '';
  }
}

// ⭐ Ruta de ejecución (pedido de Angelo, 2026-08-07, estilo GHL): arma la
// línea de detalle de UN paso ya ejecutado a partir de lo que guardó
// logRunStep en el backend (crm_workflow_run_steps.detail, ver
// workflowEngine.js). Reusa las mismas listas de referencia (tags/etapas)
// que ya tiene cargadas WorkflowEditor -- así "Agregó etiqueta" muestra el
// NOMBRE de la etiqueta, no solo su id.
function describeRunStepDetail(stepType, detail, { pipelineStages, tags }) {
  if (!detail) return '';
  switch (stepType) {
    case 'send_message':
      return detail.preview ? `"${detail.preview}"` : 'Mensaje enviado';
    case 'internal_notification': {
      const recipients = Array.isArray(detail.recipients) ? detail.recipients.length : 0;
      const ok = recipients - (detail.failedCount || 0);
      return `${detail.preview ? `"${detail.preview}" · ` : ''}${ok}/${recipients} destinatario(s)`;
    }
    case 'wait':
      // ⭐ Dos formas distintas de este paso en el Historial (2026-08-09):
      // cuando la corrida ENTRA a la espera (trae waitMinutes), y cuando SALE
      // de ella (trae la rama que se tomó). Antes la salida mostraba
      // "Esperó undefined min", que no decía nada de lo que realmente pasó.
      if (detail.resumed) {
        const via = detail.resumed === 'reply' ? 'El contacto respondió' : 'Se agotó el tiempo de espera';
        if (detail.endedHere) {
          return `${via} → rama "${detail.branchLabel}" SIN CONECTAR: el flujo terminó acá`;
        }
        return `${via} → sigue por "${detail.branchLabel}"`;
      }
      if (detail.mode === 'until_reply') {
        return `Esperando respuesta (máx ${detail.waitMinutes} min)`;
      }
      return `Esperó ${detail.waitMinutes} min`;
    case 'add_tag':
    case 'remove_tag': {
      const tag = tags.find(t => String(t.id) === String(detail.tagId));
      return tag ? tag.name : (detail.tagId ? `Etiqueta #${detail.tagId}` : 'Sin etiqueta');
    }
    case 'set_stage': {
      if (!detail.stageId) return 'Volvió a "Sin etapa"';
      const stage = pipelineStages.find(s => String(s.id) === String(detail.stageId));
      return stage?.name || `Etapa #${detail.stageId}`;
    }
    case 'set_temperature':
      return detail.temperature ? `Temperatura → ${TEMP_LABELS[detail.temperature] || detail.temperature}` : 'Sin cambio (valor inválido)';
    case 'condition': {
      if (detail.field === 'message_type') {
        const matchedLabel = detail.matchedBranch
          ? (MESSAGE_TYPE_BRANCH_META.find(m => m.key === detail.matchedBranch)?.label || detail.matchedBranch)
          : 'ninguna rama (sin match)';
        return `Tipo de mensaje · ${matchedLabel} →`;
      }
      const fieldLabels = { stage: 'Etapa', tag: 'Etiqueta', temperature: 'Temperatura', message: 'Mensaje' };
      const branchLabel = detail.branch === 'true' ? 'Sí →' : 'No →';
      return `${fieldLabels[detail.field] || detail.field || '...'} · ${branchLabel}`;
    }
    case 'ai_reply':
      // ⭐ 'skipped' (Workflows v2): el toggle "pausar si humano respondió"
      // hizo que este paso NO escribiera nada -- distinguirlo claramente de
      // "la IA respondió" en el Historial, para que no parezca un error.
      return detail.skipped ? 'Se saltó -- ya había respondido un agente humano' : 'La IA respondió';
    case 'branch': {
      if (detail.branch !== 'sin_match') return `Rama · ${detail.branch} →`;
      // ⭐ Sin match: se dice CONTRA QUÉ se comparó (2026-08-10). "ninguna
      // coincidió" a secas no permitía distinguir "el cliente escribió otra
      // cosa" de "era un audio sin transcribir" (texto vacío).
      const txt = (detail.comparedText || '').trim();
      let why;
      if (txt) why = `no coincidió con el último mensaje: "${txt}"`;
      else if (detail.comparedType && detail.comparedType !== 'text') {
        why = `el último mensaje del contacto es un ${detail.comparedType} sin texto (¿falta transcribirlo con un nodo de IA antes?)`;
      } else why = 'no hay ningún mensaje del contacto para comparar';
      // ⭐ Detalle regla por regla (2026-08-10). El caso que motivó esto: una
      // rama con dos reglas "contiene" unidas por Y (todas) -- el mensaje traía
      // una de las dos palabras, así que la rama fallaba aunque a simple vista
      // "peluqueria" estuviera ahí. Sin ver qué regla falló era invisible.
      const diag = Array.isArray(detail.diag) ? detail.diag : [];
      const detalle = diag.map((b) => {
        // Se intercala el conector real de cada hueco, no uno global.
        const reglas = (b.rules || [])
          .map((r, i) => `${i > 0 ? ` ${r.join || 'AND'} ` : ''}${r.ok ? '✓' : '✗'} "${r.value}"`)
          .join('');
        return `${b.label}: ${reglas}`;
      }).join(' · ');
      return `Ninguna rama ${why}${detalle ? ` — ${detalle}` : ''}${detail.noOutgoing ? ' · y la salida "ninguna" está SIN CONECTAR: el flujo terminó acá' : ' →'}`;
    }
    case 'business_hours':
      return detail.withinHours ? 'Dentro de horario →' : 'Fuera de horario →';
    case 'update_field': {
      const FIELD_LABELS = { name: 'Nombre', email: 'Email', cedula: 'Cédula', sector: 'Sector', birthday: 'Cumpleaños' };
      return `${FIELD_LABELS[detail.field] || detail.field} → "${detail.value || ''}"`;
    }
    case 'end_goal':
      return `Meta alcanzada: "${detail.goal}"`;
    case 'ai_intent':
      return `Intención · ${detail.branch === 'ninguna' ? 'ninguna coincidió' : detail.branch} →`;
    default:
      return '';
  }
}

// ── Nodos custom del canvas ──
const TriggerNodeComponent = ({ data }) => (
  <div className="px-4 py-3 rounded-2xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-white shadow-md w-56">
    <div className="flex items-center gap-2">
      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600 text-white flex-shrink-0 shadow-sm">
        <Zap className="w-3.5 h-3.5" />
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700">Disparador</span>
    </div>
    <p className="text-sm font-semibold text-gray-800 mt-1.5 truncate">{TRIGGER_LABELS[data.triggerType] || 'Sin configurar'}</p>
    <p className="text-xs text-gray-500 mt-0.5 truncate">{data.summary}</p>
    <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3" />
  </div>
);

const ActionNodeComponent = ({ id, data, selected }) => {
  const meta = STEP_META[data.stepType] || {};
  const Icon = meta.icon || Zap;
  const colors = STEP_COLOR_CLASSES[meta.color] || STEP_COLOR_CLASSES.gray;
  const isWaitReply = data.stepType === 'wait' && data.config?.mode === 'until_reply';
  // Lo calcula el editor y lo baja por `data` (ver el useEffect de
  // missingBranches): el componente del nodo no tiene acceso a las aristas.
  const missing = isWaitReply && Array.isArray(data.missingBranches) ? data.missingBranches : [];
  return (
    <div className={`relative px-3.5 py-3 rounded-2xl border-2 bg-white shadow-md hover:shadow-lg transition-shadow w-56 ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}>
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-3 !h-3" />
      <NodeMenuButton nodeId={id} />
      <div className="flex items-center gap-2 pr-5">
        <span className={`flex items-center justify-center w-6 h-6 rounded-lg ${colors.bg} ${colors.text} flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 truncate">{meta.label || 'Paso'}</span>
      </div>
      <p className="text-sm text-gray-700 mt-1.5 truncate">{data.summary}</p>
      {/* ⭐ "Esperar hasta que responda" tiene DOS salidas (2026-08-09, pregunta
          de Angelo: "¿y si responde?"). Antes tenía una sola, así que respondiera
          o no el flujo seguía por el mismo camino y la única diferencia era
          cuándo. Los ids de handle son 'true'/'false' para reusar
          branch_true_step_id/branch_false_step_id del backend, igual que la
          Condición -- no hace falta ninguna columna nueva en los pasos. */}
      {isWaitReply ? (
        <>
          {/* ⭐ Aviso en el propio nodo (2026-08-09). Con varios nodos de espera
              en el mismo flujo, saber que "una rama quedó suelta" no alcanza:
              hay que poder ver CUÁL de un vistazo, sin abrir cada uno. La rama
              sin conectar se pinta en ámbar y su patita queda hueca. */}
          {missing.length > 0 && (
            <p className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-2 leading-tight">
              ⚠️ {missing.length === 2 ? 'Ninguna salida conectada' : `"${missing[0]}" sin conectar`}: el flujo termina acá
            </p>
          )}
          <div className="flex justify-between text-[9px] font-semibold px-1 mt-2">
            <span className={missing.includes('Respondió') ? 'text-amber-600' : 'text-green-600'}>Respondió</span>
            <span className={missing.includes('No respondió') ? 'text-amber-600' : 'text-gray-500'}>No respondió</span>
          </div>
          <Handle type="source" position={Position.Bottom} id="true" style={{ left: '28%' }}
            className={missing.includes('Respondió') ? '!bg-white !border-2 !border-amber-500 !w-3 !h-3' : '!bg-green-500 !w-3 !h-3'} />
          <Handle type="source" position={Position.Bottom} id="false" style={{ left: '72%' }}
            className={missing.includes('No respondió') ? '!bg-white !border-2 !border-amber-500 !w-3 !h-3' : '!bg-gray-400 !w-3 !h-3'} />
        </>
      ) : (
        <>
          {/* ⭐ Callejón sin salida (2026-08-10). Este paso no tiene ningún paso
              siguiente conectado, así que la corrida termina acá aunque el flujo
              siga dibujado más abajo por otra rama. Antes se veía idéntico a un
              paso normal y la corrida figuraba como "Completado". */}
          {data.deadEnd && (
            <p className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-2 leading-tight">
              ⚠️ Sin paso siguiente: el flujo termina acá
            </p>
          )}
          <Handle type="source" position={Position.Bottom}
            className={data.deadEnd ? '!bg-white !border-2 !border-amber-500 !w-3 !h-3' : '!bg-gray-400 !w-3 !h-3'} />
        </>
      )}
    </div>
  );
};

// ⭐ Condición multi-rama por tipo de mensaje (pedido de Angelo,
// 2026-08-07): a diferencia de las demás condiciones (siempre 2 salidas
// fijas, Sí/No), esta puede tener entre 1 y 7 salidas activas (las que el
// usuario eligió en `config.branches`) más una salida extra "otro" para
// cuando el tipo del último mensaje no es ninguna de las elegidas -- esa
// usa el id 'false' a propósito, para reusar branch_false_step_id del lado
// del backend sin sumar una columna nueva solo para este caso (ver
// evaluateMessageTypeBranch en workflowEngine.js). Los ids de handle de las
// ramas activas van prefijados `mt_` (mt_video, mt_text, ...) para que el
// resto del editor (carga/guardado de edges) los distinga de 'true'/'false'.
const ConditionNodeComponent = ({ id, data, selected }) => {
  // ⭐ 'business_hours' (Workflows v2) reusa este mismo componente -- misma
  // forma (2 salidas fijas), solo cambian colores/labels/ícono para que se
  // distinga a simple vista de una 'Condición' común (Dentro/Fuera en vez
  // de Sí/No, ámbar->teal).
  const isBusinessHours = data.stepType === 'business_hours';
  const isMessageType = !isBusinessHours && data.config?.field === 'message_type';
  const activeBranches = isMessageType && Array.isArray(data.config.branches) ? data.config.branches : [];
  const slots = activeBranches.length + 1; // +1 = salida "otro"/sin match
  // ⭐ Mismo fix de ancho dinámico que BranchNodeComponent: acá el problema es
  // idéntico cuando se activan 4+ tipos de mensaje. Con 2 salidas fijas
  // (condición normal / horario laboral) el resultado es 224px, o sea el mismo
  // ancho de siempre.
  const width = isMessageType ? branchNodeWidth(slots) : NODE_MIN_WIDTH;

  return (
    <div
      style={{ width }}
      className={`relative px-4 py-3 rounded-2xl border-2 shadow-md hover:shadow-lg transition-shadow ${isBusinessHours ? 'bg-teal-50' : 'bg-yellow-50'} ${selected ? 'border-blue-500 ring-2 ring-blue-200' : (isBusinessHours ? 'border-teal-400' : 'border-yellow-400')}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-3 !h-3" />
      <NodeMenuButton nodeId={id} />
      <div className={`flex items-center gap-1.5 pr-5 text-[10px] font-bold uppercase tracking-wide ${isBusinessHours ? 'text-teal-700' : 'text-yellow-700'}`}>
        {isBusinessHours ? <Sun className="w-3.5 h-3.5" /> : <GitBranch className="w-3.5 h-3.5" />} {isBusinessHours ? 'Horario laboral' : 'Condición'}
      </div>
      <p className="text-sm text-gray-700 mt-1 truncate">{data.summary}</p>

      {isBusinessHours ? (
        <>
          <div className="flex justify-between text-[9px] font-semibold px-1 mt-2">
            <span className="text-teal-600">Dentro</span><span className="text-gray-500">Fuera</span>
          </div>
          <Handle type="source" position={Position.Bottom} id="true" style={{ left: '30%' }} className="!bg-teal-500 !w-3 !h-3" />
          <Handle type="source" position={Position.Bottom} id="false" style={{ left: '70%' }} className="!bg-gray-400 !w-3 !h-3" />
        </>
      ) : isMessageType ? (
        activeBranches.length === 0 ? (
          <p className="text-[9px] text-gray-400 mt-2">Elegí al menos un tipo de mensaje.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1 mt-2 mb-3">
              {activeBranches.map((key) => {
                const meta = MESSAGE_TYPE_BRANCH_META.find(m => m.key === key);
                return <span key={key} className="text-[9px] font-semibold px-1 py-0.5 rounded bg-yellow-100 text-yellow-800">{meta?.emoji} {meta?.label || key}</span>;
              })}
              <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-500">otro</span>
            </div>
            {activeBranches.map((key, i) => (
              <Handle
                key={key}
                type="source"
                position={Position.Bottom}
                id={`mt_${key}`}
                style={{ left: `${((i + 1) / (slots + 1)) * 100}%` }}
                className="!bg-yellow-500 !w-3 !h-3"
              />
            ))}
            <Handle
              type="source"
              position={Position.Bottom}
              id="false"
              style={{ left: `${(slots / (slots + 1)) * 100}%` }}
              className="!bg-gray-400 !w-3 !h-3"
            />
          </>
        )
      ) : (
        <>
          <div className="flex justify-between text-[9px] font-semibold px-1 mt-2">
            <span className="text-green-600">Sí</span><span className="text-red-500">No</span>
          </div>
          <Handle type="source" position={Position.Bottom} id="true" style={{ left: '30%' }} className="!bg-green-500 !w-3 !h-3" />
          <Handle type="source" position={Position.Bottom} id="false" style={{ left: '70%' }} className="!bg-red-500 !w-3 !h-3" />
        </>
      )}
    </div>
  );
};

// ⭐ Workflows v2 -- lista unificada de "salidas con nombre" de un nodo
// 'branch' o 'ai_intent', usada tanto para pintar los handles del nodo
// (BranchNodeComponent) como para armar branchMap al guardar (handleSave) y
// reconstruir los edges al cargar (load). 'branch' guarda sus ramas en
// config.branches ({key,label,...}), 'ai_intent' en config.intents
// ({key,name,description}) -- ambas se homogeneízan acá a {key,label} para
// no tener 2 caminos de código distintos en el resto del editor.
// ⭐ Workflows v2 -- nombres de días para el panel de 'business_hours',
// indexados IGUAL que Date.getUTCDay()/getDay() (0=domingo..6=sábado), que
// es el mismo índice que usa evaluateBusinessHours del lado del backend.
const WEEKDAY_LABELS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// ⭐ Genera una key estable (slug) para una rama/intención nueva, sin
// choques con las que ya existen en el nodo -- la key NO cambia si el
// usuario después le edita el nombre visible (label/name), justo para no
// romper los handles/edges ya conectados en el canvas (ver br_<key> en
// BranchNodeComponent).
function slugifyBranchKey(label, existingKeys) {
  const base = String(label || 'rama').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'rama';
  let key = base;
  let n = 2;
  while (existingKeys.includes(key)) { key = `${base}_${n++}`; }
  return key;
}

function getBranchSlots(stepType, config) {
  if (stepType === 'ai_intent') {
    return (Array.isArray(config?.intents) ? config.intents : [])
      .filter((i) => i && i.key && i.name)
      .map((i) => ({ key: i.key, label: i.name }));
  }
  return (Array.isArray(config?.branches) ? config.branches : [])
    .filter((b) => b && b.key)
    .map((b) => ({ key: b.key, label: config?.mode === 'random' ? `${b.label || b.key} (${b.weight || 0}%)` : (b.label || b.key) }));
}

// ⭐ Nodo 'Rama múltiple' / 'Detectar intención (IA)' (Workflows v2, pedido
// de Angelo tipo GHL: "que se pueda personalizar hasta el no"). A
// diferencia de ConditionNodeComponent (2 salidas fijas, o N salidas fijas
// del vocabulario cerrado de tipos de mensaje), acá las salidas las nombra
// el usuario libremente -- por eso necesita su propio componente en vez de
// reusar el de arriba. Los ids de handle van prefijados `br_` (br_interesado,
// br_no_interesado...) para no chocar con `mt_` (tipo de mensaje) ni con
// true/false (condición/horario) en el resto del editor.
// ⭐ Ancho de un nodo con N salidas. 224px es el ancho histórico (w-56) y se
// mantiene como piso para que un nodo de 2 salidas se vea igual que siempre;
// a partir de ahí se suman 60px por salida, que es lo que hace falta para que
// dos patitas de 12px queden separadas y se puedan arrastrar sin pisarse.
const NODE_MIN_WIDTH = 224;
const NODE_OUTPUT_SPACING = 60;
const branchNodeWidth = (outputs) => Math.max(NODE_MIN_WIDTH, (outputs + 1) * NODE_OUTPUT_SPACING);

const BranchNodeComponent = ({ id, data, selected }) => {
  const isIntent = data.stepType === 'ai_intent';
  const slots = getBranchSlots(data.stepType, data.config || {});
  const total = slots.length + 1; // +1 = salida "sin match"/"ninguna"
  const Icon = isIntent ? Sparkles : Shuffle;
  // ⭐ FIX (reporte de Angelo, 2026-08-09: "a partir de la 3ra rama no me deja
  // extender la patita"). El nodo tenía ancho FIJO (w-56 = 224px) sin importar
  // cuántas salidas tuviera. Las patitas se reparten con `left: n/(total+1)`,
  // así que con 4 ramas + "sin match" quedaban a ~37px una de otra siendo de
  // 12px cada una: pegadas, imposibles de agarrar por separado, y arrastrar
  // una terminaba tomando la de al lado. Ahora el ancho crece con el número de
  // salidas (NODE_OUTPUT_SPACING px por salida) con un piso de 224px, así que
  // la separación entre patitas es constante sin importar cuántas ramas haya.
  const width = branchNodeWidth(total);
  const wrapClass = isIntent
    ? `relative px-4 py-3 rounded-2xl border-2 bg-purple-50 shadow-md hover:shadow-lg transition-shadow ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-purple-400'}`
    : `relative px-4 py-3 rounded-2xl border-2 bg-indigo-50 shadow-md hover:shadow-lg transition-shadow ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-indigo-400'}`;
  const labelClass = isIntent ? 'text-purple-700' : 'text-indigo-700';
  const chipClass = isIntent ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800';
  const dotClass = isIntent ? '!bg-purple-500 !w-3 !h-3' : '!bg-indigo-500 !w-3 !h-3';

  return (
    <div className={wrapClass} style={{ width }}>
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-3 !h-3" />
      <NodeMenuButton nodeId={id} />
      <div className={`flex items-center gap-1.5 pr-5 text-[10px] font-bold uppercase tracking-wide ${labelClass}`}>
        <Icon className="w-3.5 h-3.5" /> {isIntent ? 'Detectar intención' : 'Rama múltiple'}
      </div>
      <p className="text-sm text-gray-700 mt-1 truncate">{data.summary}</p>

      {slots.length === 0 ? (
        <p className="text-[9px] text-gray-400 mt-2">{isIntent ? 'Agregá al menos una intención en el panel.' : 'Agregá al menos una rama en el panel.'}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 mt-2 mb-3">
            {slots.map((s) => <span key={s.key} className={`text-[9px] font-semibold px-1 py-0.5 rounded ${chipClass}`}>{s.label}</span>)}
            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-500">{isIntent ? 'ninguna' : 'sin match'}</span>
          </div>
          {slots.map((s, i) => (
            <Handle
              key={s.key}
              type="source"
              position={Position.Bottom}
              id={`br_${s.key}`}
              style={{ left: `${((i + 1) / (total + 1)) * 100}%` }}
              className={dotClass}
            />
          ))}
          <Handle type="source" position={Position.Bottom} id="false" style={{ left: `${(total / (total + 1)) * 100}%` }} className="!bg-gray-400 !w-3 !h-3" />
        </>
      )}
    </div>
  );
};

// ⭐ Botón + dropdown de valores personalizados (pedido de Angelo,
// 2026-08-07), reusado en 3 lugares de este archivo: el textarea de
// "Enviar mensaje", el de "Notificación interna", y el editor de
// plantillas (TemplatesModal, más abajo). Self-contenido (su propio
// open/close) para poder usarse varias veces en el mismo panel sin pisarse
// estado entre instancias -- cada uno recibe SU ref/value/onChange.
const CustomValueButton = ({ textareaRef, value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Insertar valor personalizado"
        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
      >
        <Braces className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute z-20 top-full right-0 mt-1 w-56 max-h-52 overflow-y-auto bg-white border rounded-lg shadow-lg">
          {CUSTOM_VALUES.map((cv) => (
            <button
              key={cv.key}
              type="button"
              onClick={() => { insertAtCursor(textareaRef, value, onChange, `{{${cv.key}}}`); setOpen(false); }}
              className="w-full text-left px-2.5 py-1.5 hover:bg-gray-50 border-b last:border-b-0"
            >
              <p className="text-[11px] font-semibold text-gray-800">{cv.label}</p>
              <p className="text-[10px] text-gray-400">{`{{${cv.key}}}`}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Definido en el módulo (no dentro del componente) -- React Flow avisa por
// consola si `nodeTypes` cambia de referencia en cada render, porque eso
// fuerza que reconstruya internamente su registro de tipos.
const nodeTypes = { triggerNode: TriggerNodeComponent, actionNode: ActionNodeComponent, conditionNode: ConditionNodeComponent, branchNode: BranchNodeComponent };

/**
 * ⭐ Menú "⋯" del nodo, estilo GHL (pedido de Angelo, 2026-08-10).
 *
 * Los componentes de nodo están definidos a nivel de módulo (obligatorio, ver
 * el comentario de `nodeTypes` arriba), así que NO pueden recibir el setter del
 * menú por props ni por contexto sin volver a crear `nodeTypes` en cada render.
 * Este puente de una sola línea lo resuelve: el editor registra su handler al
 * montarse y los nodos lo llaman. Sirve porque hay UN solo editor abierto a la
 * vez -- si alguna vez hubiera dos, esto tendría que pasar a un Context.
 */
let openNodeMenuBridge = null;
const NodeMenuButton = ({ nodeId }) => (
  <button
    type="button"
    title="Acciones"
    onClick={(e) => {
      e.stopPropagation();
      const r = e.currentTarget.getBoundingClientRect();
      if (openNodeMenuBridge) openNodeMenuBridge(nodeId, r.right, r.bottom);
    }}
    className="nodrag absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 leading-none"
  >
    ⋯
  </button>
);

/**
 * ⭐ Envío de prueba desde el panel del paso (pedido de Angelo, 2026-08-10).
 *
 * Sirve para dos cosas distintas, y por eso manda de verdad en vez de simular:
 *   1. ver cómo LLEGA el mensaje (plantilla con adjunto, saltos de línea,
 *      valores {{contact.*}} ya reemplazados);
 *   2. confirmar que el canal conectado responde y las credenciales sirven.
 *
 * Manda lo que hay guardado en el nodo EN ESTE MOMENTO, no lo último guardado
 * en el servidor -- así se puede probar un texto antes de decidir guardarlo.
 */
const TestSendBox = ({ templateId, content }) => {
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // {ok, msg}

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await apiClient.post('/api/pos/crm/workflows/test-send', {
        phone,
        content: content || '',
        templateId: templateId || null,
      });
      setResult({
        ok: true,
        msg: `Enviado a ${res.data.sentTo}${res.data.hasMedia ? ` con adjunto (${res.data.mediaKind})` : ''} por ${res.data.provider}.`,
      });
    } catch (err) {
      // El backend devuelve el motivo real (canal caído, plantilla borrada,
      // credenciales vencidas) -- es justo lo que se quiere leer acá.
      setResult({ ok: false, msg: err.response?.data?.error || 'No se pudo enviar la prueba.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-dashed border-emerald-300 bg-emerald-50/50 rounded-lg p-2 space-y-1.5">
      <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">Probar este mensaje</p>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="593987654321"
          className="flex-1 px-2 py-1 border border-emerald-300 rounded text-xs"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !phone.trim()}
          className="px-3 py-1 rounded text-xs font-semibold bg-emerald-600 text-white disabled:opacity-40"
        >
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
      <p className="text-[10px] text-gray-400">
        Con código de país. Se manda por el primer canal de WhatsApp activo, con la plantilla y el adjunto reales. No queda registrado en Conversaciones.
      </p>
      {result && (
        <p className={`text-[10px] rounded px-1.5 py-1 ${result.ok ? 'text-emerald-800 bg-emerald-100' : 'text-red-700 bg-red-50 border border-red-200'}`}>
          {result.ok ? '✅ ' : '⚠️ '}{result.msg}
        </p>
      )}
    </div>
  );
};

// ⭐ Workflows v2 -- a qué tipo de nodo visual (de los 4 registrados arriba)
// corresponde cada step type del backend. Un solo lugar para esta regla,
// usado tanto al cargar (load) como al agregar un paso nuevo (addStepNode)
// para que nunca queden desincronizados entre sí.
const stepNodeType = (stepType) => {
  if (stepType === 'condition' || stepType === 'business_hours') return 'conditionNode';
  if (stepType === 'branch' || stepType === 'ai_intent') return 'branchNode';
  return 'actionNode';
};

// ⭐ Editor visual de un workflow puntual. Recibe las listas de referencia
// (tags/etapas/plantillas) ya cargadas por el padre -- evita que cada
// apertura del editor dispare las mismas 3 requests de nuevo.
const WorkflowEditor = ({ workflowId, tags, allWorkflows, pipelines, pipelineStages, templates, aiContexts, hasAiAccess, employees, onBack, onManageTemplates }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  // Menú "⋯" del nodo: {nodeId, x, y} en coordenadas de pantalla, o null.
  const [nodeMenu, setNodeMenu] = useState(null);
  // Portapapeles de nodos. Vive en el editor (no en localStorage) a propósito:
  // pegar en OTRO workflow requeriría revalidar ids de etapas/etiquetas/
  // plantillas del destino, que es justo lo que ya resuelve Plantillas.
  const [clipboard, setClipboard] = useState(null);
  const [workflowMeta, setWorkflowMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  // ⭐ Refs para el picker de valores personalizados (ver CustomValueButton
  // más arriba) -- uno por cada textarea que puede usar {{contact.xxx}}.
  const sendMessageContentRef = useRef(null);
  const internalNotifContentRef = useRef(null);
  // ⭐ Workflows v2: valor a escribir del nodo 'Actualizar campo' -- también
  // acepta valores personalizados {{contact.xxx}}.
  const updateFieldValueRef = useRef(null);
  const [runs, setRuns] = useState([]);
  // ⭐ Ruta de ejecución (pedido de Angelo, 2026-08-07: "estilo GHL, al dar
  // click en una acción que ya se ejecutó, sale la ruta de trabajo"):
  // `expandedRunId` es la corrida actualmente desplegada dentro del modal de
  // Historial, `runSteps` cachea por run_id lo que devuelve
  // GET /crm/workflow-runs/:id/steps para no re-pedirlo si el usuario
  // colapsa y vuelve a abrir la misma corrida.
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [runSteps, setRunSteps] = useState({});
  const [loadingRunSteps, setLoadingRunSteps] = useState(false);
  const [customAlert, setCustomAlert] = useState(null);
  const nodeIdCounter = useRef(0);

  // ⭐ Contador de uso diario de IA (pedido de Angelo, 2026-08-07): antes
  // vivía en Configuracion.js, que se quitó porque tener la base de
  // conocimientos editable en 2 lugares (Configuración Y acá) hacía que se
  // mezclaran -- ahora el nodo de IA es el ÚNICO lugar para todo lo de IA,
  // incluido este contador informativo. Mismo endpoint que usaba
  // Configuracion.js (GET /api/pos/crm/ai-settings), solo lectura acá.
  const [aiUsage, setAiUsage] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (!hasAiAccess) return;
    apiClient.get('/api/pos/crm/ai-settings').then(res => setAiUsage(res.data)).catch(() => {});
  }, [hasAiAccess]);

  useEffect(() => {
    apiClient.get('/api/pos/crm/custom-fields').then(res => setCustomFields(res.data || [])).catch(() => {});
    apiClient.get('/api/pos/crm/locations').then(res => setLocations(res.data || [])).catch(() => {});
  }, []);

  // ⭐ Audio de prueba (pedido de Angelo, 2026-08-08: "¿no hay manera de
  // probar la conexión como con la API de DeepSeek?") -- el chat de texto
  // se prueba con un ping, pero el TTS solo se confirma escuchando una nota
  // de voz real. Los canales se cargan acá (no existían en este componente
  // todavía) SOLO para armar el selector de este botón -- mismo endpoint
  // que ya usa la gestión de canales en Conversaciones.js.
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    if (!hasAiAccess) return;
    apiClient.get('/api/pos/crm/channels').then(res => setChannels(res.data || [])).catch(() => {});
  }, [hasAiAccess]);
  const connectedChannels = channels.filter(c => c.provider === 'baileys' && c.liveStatus === 'open');
  const [testAudioChannelId, setTestAudioChannelId] = useState('');
  const [testAudioPhone, setTestAudioPhone] = useState('');
  const [testingAudio, setTestingAudio] = useState(false);
  const [testAudioResult, setTestAudioResult] = useState(null); // { ok, error? } | null

  const handleTestAudio = async () => {
    setTestingAudio(true);
    setTestAudioResult(null);
    try {
      const res = await apiClient.post('/api/pos/crm/ai/test-audio', {
        channelId: testAudioChannelId,
        phone: testAudioPhone,
      });
      if (res.data.success) {
        setTestAudioResult({ ok: true });
      } else {
        setTestAudioResult({ ok: false, error: res.data.error || 'No se pudo mandar el audio de prueba.' });
      }
    } catch (err) {
      setTestAudioResult({ ok: false, error: err.response?.data?.error || 'Error al mandar el audio de prueba.' });
    } finally {
      setTestingAudio(false);
    }
  };

  // ⭐ Renombrar workflow (bug reportado por Angelo, 2026-08-06: "no se
  // puede cambiar el nombre de un workflow"). El backend (PUT
  // /api/pos/crm/workflows/:id) ya aceptaba `name` desde siempre -- lo que
  // faltaba era la UI. Mismo patrón de click-to-edit que el rename de
  // pipelines en PipelineManager.js, para consistencia visual.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const startEditName = () => {
    setNameDraft(workflowMeta?.name || '');
    setEditingName(true);
  };

  const saveRenameWorkflow = async () => {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed || trimmed === workflowMeta?.name) return;
    try {
      await apiClient.put(`/api/pos/crm/workflows/${workflowId}`, { name: trimmed });
      setWorkflowMeta(prev => prev ? { ...prev, name: trimmed } : prev);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo renombrar el workflow.');
    }
  };

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/pos/crm/workflows/${workflowId}`);
      const wf = res.data;
      setWorkflowMeta(wf);
      const triggerCfg = safeParse(wf.trigger_config);
      const triggerNode = {
        id: 'trigger',
        type: 'triggerNode',
        position: { x: 120, y: 0 },
        draggable: false,
        deletable: false,
        data: { kind: 'trigger', triggerType: wf.trigger_type, config: triggerCfg, summary: describeTrigger(wf.trigger_type, triggerCfg, { pipelineStages, tags }) }
      };
      const stepNodes = (wf.steps || []).map((s) => {
        const cfg = safeParse(s.config);
        return {
          id: String(s.id),
          type: stepNodeType(s.type),
          position: { x: s.position_x || 120, y: s.position_y || 160 },
          data: { kind: 'step', stepType: s.type, config: cfg, summary: describeStep(s.type, cfg, { pipelineStages, tags, templates, aiContexts }) }
        };
      });
      const newEdges = [];
      if (wf.first_step_id) newEdges.push({ id: `trigger-${wf.first_step_id}`, source: 'trigger', target: String(wf.first_step_id) });
      (wf.steps || []).forEach((s) => {
        if (s.type === 'condition') {
          const cfg = safeParse(s.config);
          if (cfg.field === 'message_type') {
            // ⭐ N ramas (ver config.branchTargets, resuelto por el backend
            // al guardar -- ver PUT /crm/workflows/:id/steps) en vez de las
            // 2 columnas fijas de abajo. branch_false_step_id se reusa acá
            // como la salida "otro"/sin match (id de handle 'false', igual
            // que el resto de las condiciones -- así el resto del editor
            // no necesita saber que esta es distinta).
            const targets = cfg.branchTargets || {};
            (Array.isArray(cfg.branches) ? cfg.branches : []).forEach((key) => {
              const targetId = targets[key];
              if (targetId) {
                const meta = MESSAGE_TYPE_BRANCH_META.find(m => m.key === key);
                newEdges.push({ id: `e-${s.id}-mt-${key}`, source: String(s.id), sourceHandle: `mt_${key}`, target: String(targetId), label: meta?.label || key, style: { stroke: '#ca8a04' } });
              }
            });
            if (s.branch_false_step_id) newEdges.push({ id: `e-${s.id}-f`, source: String(s.id), sourceHandle: 'false', target: String(s.branch_false_step_id), label: 'otro', style: { stroke: '#9ca3af' } });
          } else {
            if (s.branch_true_step_id) newEdges.push({ id: `e-${s.id}-t`, source: String(s.id), sourceHandle: 'true', target: String(s.branch_true_step_id), label: 'Sí', style: { stroke: '#22c55e' } });
            if (s.branch_false_step_id) newEdges.push({ id: `e-${s.id}-f`, source: String(s.id), sourceHandle: 'false', target: String(s.branch_false_step_id), label: 'No', style: { stroke: '#ef4444' } });
          }
        } else if (s.type === 'branch' || s.type === 'ai_intent') {
          // ⭐ Workflows v2: mismo mecanismo que message_type de arriba
          // (config.branchTargets + salida 'false' de respaldo), pero con
          // nombres de rama libres definidos por el usuario en vez de un
          // vocabulario cerrado -- ver getBranchSlots.
          const cfg = safeParse(s.config);
          const targets = cfg.branchTargets || {};
          getBranchSlots(s.type, cfg).forEach((slot) => {
            const targetId = targets[slot.key];
            if (targetId) {
              newEdges.push({ id: `e-${s.id}-br-${slot.key}`, source: String(s.id), sourceHandle: `br_${slot.key}`, target: String(targetId), label: slot.label, style: { stroke: '#6366f1' } });
            }
          });
          if (s.branch_false_step_id) newEdges.push({ id: `e-${s.id}-f`, source: String(s.id), sourceHandle: 'false', target: String(s.branch_false_step_id), label: s.type === 'ai_intent' ? 'ninguna' : 'sin match', style: { stroke: '#9ca3af' } });
        } else if (s.type === 'business_hours') {
          if (s.branch_true_step_id) newEdges.push({ id: `e-${s.id}-t`, source: String(s.id), sourceHandle: 'true', target: String(s.branch_true_step_id), label: 'Dentro', style: { stroke: '#0d9488' } });
          if (s.branch_false_step_id) newEdges.push({ id: `e-${s.id}-f`, source: String(s.id), sourceHandle: 'false', target: String(s.branch_false_step_id), label: 'Fuera', style: { stroke: '#9ca3af' } });
        } else if (s.type === 'wait' && safeParse(s.config).mode === 'until_reply') {
          // ⭐ Dos salidas del nodo de espera-por-respuesta (2026-08-09).
          if (s.branch_true_step_id) newEdges.push({ id: `e-${s.id}-t`, source: String(s.id), sourceHandle: 'true', target: String(s.branch_true_step_id), label: 'Respondió', style: { stroke: '#22c55e' } });
          if (s.branch_false_step_id) newEdges.push({ id: `e-${s.id}-f`, source: String(s.id), sourceHandle: 'false', target: String(s.branch_false_step_id), label: 'No respondió', style: { stroke: '#9ca3af' } });
          // Workflows viejos: una sola salida por next_step_id, se dibuja igual.
          if (!s.branch_true_step_id && !s.branch_false_step_id && s.next_step_id) {
            newEdges.push({ id: `e-${s.id}-n`, source: String(s.id), target: String(s.next_step_id) });
          }
        } else if (s.next_step_id) {
          newEdges.push({ id: `e-${s.id}-n`, source: String(s.id), target: String(s.next_step_id) });
        }
      });
      setNodes([triggerNode, ...stepNodes]);
      setEdges(newEdges);
      setDirty(false);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo cargar el workflow.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, pipelineStages, tags, templates, setNodes, setEdges]);

  useEffect(() => { load(); }, [load]);

  // Si el nodo seleccionado se borra (ej. con Backspace desde el canvas),
  // el panel de configuración de la derecha no debe quedar apuntando a un
  // nodo fantasma.
  useEffect(() => {
    if (selectedNodeId && !nodes.find(n => n.id === selectedNodeId)) setSelectedNodeId(null);
  }, [nodes, selectedNodeId]);

  const onConnect = useCallback((params) => {
    // ⭐ Workflows v2: el label/color del edge ahora depende del stepType del
    // nodo de ORIGEN (business_hours/branch/ai_intent cambian qué significa
    // el handle 'true'/'false', y 'br_*' necesita mirar la config del nodo
    // para saber el nombre libre que el usuario le puso a esa rama) -- por
    // eso `nodes` pasa a ser una dependencia real de este callback.
    const sourceNode = nodes.find(n => n.id === params.source);
    const sourceStepType = sourceNode?.data?.stepType;
    const handle = params.sourceHandle;
    const mtKey = handle?.startsWith('mt_') ? handle.slice(3) : null;
    const brKey = handle?.startsWith('br_') ? handle.slice(3) : null;
    let label = null;
    let stroke = '#6b7280';
    if (handle === 'true') {
      label = sourceStepType === 'business_hours' ? 'Dentro' : 'Sí';
      stroke = sourceStepType === 'business_hours' ? '#0d9488' : '#22c55e';
    } else if (mtKey) {
      label = MESSAGE_TYPE_BRANCH_META.find(m => m.key === mtKey)?.label || mtKey;
      stroke = '#ca8a04';
    } else if (brKey) {
      const slots = getBranchSlots(sourceStepType, sourceNode?.data?.config || {});
      label = slots.find(s => s.key === brKey)?.label || brKey;
      stroke = '#6366f1';
    } else if (handle === 'false') {
      label = sourceStepType === 'business_hours' ? 'Fuera'
        : sourceStepType === 'ai_intent' ? 'ninguna'
        : sourceStepType === 'branch' ? 'sin match'
        : 'No';
      stroke = '#9ca3af';
    }
    const styled = label ? { ...params, label, style: { stroke } } : params;
    setEdges((eds) => {
      // Un solo edge saliente por handle: un paso normal solo puede seguir
      // a UN siguiente paso, y una condición/rama solo a uno por salida --
      // si ya había una conexión desde este mismo origen+handle, se
      // reemplaza en vez de sumarse (evita ramas ambiguas).
      const filtered = eds.filter(e => !(e.source === params.source && e.sourceHandle === params.sourceHandle));
      return addEdge(styled, filtered);
    });
    setDirty(true);
  }, [setEdges, nodes]);

  // ⭐ Marca en cada nodo de espera qué salidas quedaron sin conectar
  // (2026-08-09). Se calcula acá, no dentro del componente del nodo, porque el
  // nodo no tiene acceso a las aristas -- se le baja por `data`.
  //
  // Solo escribe si el valor CAMBIÓ: setNodes dispara un render que vuelve a
  // correr este efecto, así que comparar antes es lo que evita el bucle.
  useEffect(() => {
    setNodes((nds) => {
      let changed = false;
      const next = nds.map((n) => {
        if (n.data?.kind !== 'step') return n;
        const out = edges.filter((e) => e.source === n.id);

        // ⭐ Ampliado (2026-08-10, tercer reporte de Angelo sobre "el flujo
        // terminó abruptamente"): NO era solo el nodo de espera. CUALQUIER paso
        // sin paso siguiente termina la corrida en silencio, y el único final
        // legítimo es "Fin / Meta". Marcamos todos esos callejones sin salida.
        let miss;
        let dead = false;
        if (n.data.stepType === 'wait' && n.data.config?.mode === 'until_reply') {
          miss = [];
          if (!out.some((e) => e.sourceHandle === 'true')) miss.push('Respondió');
          if (!out.some((e) => e.sourceHandle === 'false')) miss.push('No respondió');
        } else if (n.type === 'actionNode' && n.data.stepType !== 'end_goal') {
          dead = out.length === 0;
        }

        const prev = n.data.missingBranches;
        const sameMiss = miss
          ? Array.isArray(prev) && prev.length === miss.length && prev.every((v, i) => v === miss[i])
          : prev === undefined;
        if (sameMiss && !!n.data.deadEnd === dead) return n;
        changed = true;
        return { ...n, data: { ...n.data, missingBranches: miss, deadEnd: dead } };
      });
      return changed ? next : nds;
    });
  }, [edges, setNodes]);

  // ⭐ Fix bug (pedido de Angelo, 2026-08-05): al guardar, load() reseteaba
  // dirty a false correctamente, pero React Flow dispara onNodesChange con
  // eventos type:'dimensions' (mide cada nodo al re-renderizarlos) y
  // type:'select' (de-selecciona el nodo anterior al reemplazar el array)
  // INMEDIATAMENTE después de que load() vuelve a pintar los nodos --
  // el handler anterior marcaba dirty=true ante CUALQUIER cambio, así que
  // el badge "Sin guardar" reaparecía solo, sin que el usuario tocara nada.
  // Ahora solo cuenta como edición real: agregar/quitar un nodo, o soltar
  // un nodo tras arrastrarlo (dragging:false) -- 'dimensions' y 'select'
  // se ignoran a propósito.
  const isMeaningfulNodeChange = (changes) => changes.some(c => (
    c.type === 'add' || c.type === 'remove' || (c.type === 'position' && c.dragging === false)
  ));
  const isMeaningfulEdgeChange = (changes) => changes.some(c => c.type === 'add' || c.type === 'remove');

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    if (isMeaningfulNodeChange(changes)) setDirty(true);
  }, [onNodesChange]);
  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    if (isMeaningfulEdgeChange(changes)) setDirty(true);
  }, [onEdgesChange]);

  // ⭐ Workflows v2: config inicial por tipo -- 'branch'/'ai_intent' arrancan
  // con su lista de ramas/intenciones vacía (se llenan desde el panel), el
  // resto arranca en {} como siempre.
  const defaultStepConfig = (stepType) => {
    if (stepType === 'branch') return { mode: 'rules', branches: [] };
    if (stepType === 'ai_intent') return { intents: [] };
    if (stepType === 'ai_extract') return { targets: [], historyLimit: 15 };
    if (stepType === 'business_hours') return { schedule: {} };
    return {};
  };

  const addStepNode = (stepType) => {
    const id = `n-${Date.now()}-${nodeIdCounter.current++}`;
    const config = defaultStepConfig(stepType);
    const node = {
      id,
      type: stepNodeType(stepType),
      position: { x: 120 + (nodes.length % 3) * 240, y: 180 + Math.floor(nodes.length / 3) * 150 },
      data: { kind: 'step', stepType, config, summary: describeStep(stepType, config, { pipelineStages, tags, templates, aiContexts }) }
    };
    setNodes((nds) => [...nds, node]);
    setSelectedNodeId(id);
    setDirty(true);
  };

  const updateNodeConfig = (nodeId, patch) => {
    setNodes((nds) => nds.map((n) => {
      if (n.id !== nodeId) return n;
      const newConfig = { ...n.data.config, ...patch };
      const summary = n.data.kind === 'trigger'
        ? describeTrigger(n.data.triggerType, newConfig, { pipelineStages, tags })
        : describeStep(n.data.stepType, newConfig, { pipelineStages, tags, templates, aiContexts });
      return { ...n, data: { ...n.data, config: newConfig, summary } };
    }));
    setDirty(true);
  };

  const updateTriggerType = (triggerType) => {
    setNodes((nds) => nds.map((n) => n.id === 'trigger'
      ? { ...n, data: { ...n.data, triggerType, config: {}, summary: describeTrigger(triggerType, {}, { pipelineStages, tags }) } }
      : n));
    setDirty(true);
  };

  const deleteNode = (nodeId) => {
    if (nodeId === 'trigger') return;
    setNodes((nds) => nds.filter(n => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNodeId(null);
    setDirty(true);
  };

  // ================= Menú "⋯" del nodo, estilo GHL (2026-08-10) =================
  //
  // "desde aquí" = el nodo y TODO lo que cuelga de él siguiendo las flechas.
  // Como el flujo es un GRAFO (una rama puede reunirse con otra más abajo), el
  // recorrido lleva un `seen` -- sin eso, un flujo con dos caminos que vuelven
  // a juntarse se recorrería dos veces, y uno con un ciclo colgaría el editor.
  const collectFromHere = useCallback((rootId) => {
    const seen = new Set([rootId]);
    const queue = [rootId];
    while (queue.length) {
      const cur = queue.shift();
      for (const e of edges) {
        if (e.source === cur && !seen.has(e.target)) { seen.add(e.target); queue.push(e.target); }
      }
    }
    seen.delete('trigger'); // el disparador nunca se copia ni se borra
    return seen;
  }, [edges]);

  const copyToClipboard = useCallback((ids) => {
    const idList = [...ids];
    const picked = nodes.filter((n) => ids.has(n.id) && n.id !== 'trigger');
    if (!picked.length) return 0;
    // Solo las aristas INTERNAS al conjunto: una flecha que sale hacia un nodo
    // que no se copió no tendría destino al pegar.
    const inner = edges.filter((e) => ids.has(e.source) && ids.has(e.target));
    setClipboard({ nodes: JSON.parse(JSON.stringify(picked)), edges: JSON.parse(JSON.stringify(inner)) });
    return idList.length;
  }, [nodes, edges]);

  const pasteClipboard = useCallback(() => {
    if (!clipboard || !clipboard.nodes.length) return;
    // ids nuevos, y el mapa viejo->nuevo para poder reconstruir las flechas.
    const idMap = {};
    const stamp = Date.now();
    const newNodes = clipboard.nodes.map((n, i) => {
      const nid = `n-${stamp}-${nodeIdCounter.current++}`;
      idMap[n.id] = nid;
      return {
        ...n,
        id: nid,
        selected: false,
        position: { x: (n.position?.x || 0) + 60, y: (n.position?.y || 0) + 60 },
        data: JSON.parse(JSON.stringify(n.data)),
      };
    });
    const newEdges = clipboard.edges.map((e, i) => ({
      ...e,
      id: `e-${stamp}-${i}`,
      source: idMap[e.source],
      target: idMap[e.target],
    })).filter((e) => e.source && e.target);
    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
    setSelectedNodeId(newNodes[0].id);
    setDirty(true);
  }, [clipboard, setNodes, setEdges]);

  const deleteFromHere = useCallback((rootId) => {
    const ids = collectFromHere(rootId);
    if (!ids.size) return;
    setNodes((nds) => nds.filter((n) => !ids.has(n.id)));
    setEdges((eds) => eds.filter((e) => !ids.has(e.source) && !ids.has(e.target)));
    setSelectedNodeId(null);
    setDirty(true);
  }, [collectFromHere, setNodes, setEdges]);

  // "Mover ... desde aquí": en un lienzo libre no hace falta un modo especial,
  // alcanza con DEJAR SELECCIONADO el subárbol -- React Flow arrastra en bloque
  // todo lo seleccionado, así que se mueve el tramo entero de una.
  const selectFromHere = useCallback((rootId, onlyOne) => {
    const ids = onlyOne ? new Set([rootId]) : collectFromHere(rootId);
    setNodes((nds) => nds.map((n) => ({ ...n, selected: ids.has(n.id) })));
  }, [collectFromHere, setNodes]);

  // Los componentes de nodo están a nivel de módulo y no pueden recibir esto
  // por props (ver openNodeMenuBridge arriba). Se registra al montar y se
  // limpia al desmontar para no dejar apuntando a un editor ya cerrado.
  useEffect(() => {
    openNodeMenuBridge = (nodeId, x, y) => setNodeMenu({ nodeId, x, y });
    return () => { openNodeMenuBridge = null; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const triggerNode = nodes.find(n => n.id === 'trigger');
      await apiClient.put(`/api/pos/crm/workflows/${workflowId}`, {
        trigger_type: triggerNode.data.triggerType,
        trigger_config: triggerNode.data.config,
      });

      const edgesBySource = {};
      edges.forEach((e) => { (edgesBySource[e.source] = edgesBySource[e.source] || []).push(e); });

      const steps = nodes.filter(n => n.id !== 'trigger').map((n) => {
        const outgoing = edgesBySource[n.id] || [];
        if (n.data.stepType === 'condition') {
          const falseEdge = outgoing.find(e => e.sourceHandle === 'false');
          // ⭐ message_type: branchMap en vez de trueEdge/falseEdge (N ramas,
          // no 2) -- ver PUT /crm/workflows/:id/steps en index.js, que
          // resuelve estos tempIds a ids reales y los guarda en
          // config.branchTargets. La rama "otro"/sin match SÍ sigue usando
          // branchFalse (mismo id de handle 'false' que las demás
          // condiciones, reusa la columna existente).
          if (n.data.config?.field === 'message_type') {
            const branchMap = {};
            (Array.isArray(n.data.config.branches) ? n.data.config.branches : []).forEach((key) => {
              const edge = outgoing.find(e => e.sourceHandle === `mt_${key}`);
              branchMap[key] = edge?.target ?? null;
            });
            return { tempId: n.id, type: 'condition', config: n.data.config, position_x: n.position.x, position_y: n.position.y, branchTrue: null, branchFalse: falseEdge?.target ?? null, branchMap };
          }
          const trueEdge = outgoing.find(e => e.sourceHandle === 'true');
          return { tempId: n.id, type: 'condition', config: n.data.config, position_x: n.position.x, position_y: n.position.y, branchTrue: trueEdge?.target ?? null, branchFalse: falseEdge?.target ?? null };
        }
        if (n.data.stepType === 'branch' || n.data.stepType === 'ai_intent') {
          // ⭐ Workflows v2: mismo branchMap genérico que 'condition' con
          // message_type -- PUT /crm/workflows/:id/steps ya lo resuelve sin
          // importar el `type` del paso (ver comentario ahí).
          const falseEdge = outgoing.find(e => e.sourceHandle === 'false');
          const branchMap = {};
          getBranchSlots(n.data.stepType, n.data.config || {}).forEach((slot) => {
            const edge = outgoing.find(e => e.sourceHandle === `br_${slot.key}`);
            branchMap[slot.key] = edge?.target ?? null;
          });
          return { tempId: n.id, type: n.data.stepType, config: n.data.config, position_x: n.position.x, position_y: n.position.y, branchTrue: null, branchFalse: falseEdge?.target ?? null, branchMap };
        }
        if (n.data.stepType === 'business_hours') {
          const trueEdge = outgoing.find(e => e.sourceHandle === 'true');
          const falseEdge = outgoing.find(e => e.sourceHandle === 'false');
          return { tempId: n.id, type: n.data.stepType, config: n.data.config, position_x: n.position.x, position_y: n.position.y, branchTrue: trueEdge?.target ?? null, branchFalse: falseEdge?.target ?? null };
        }
        // ⭐ Espera-por-respuesta: se guarda como una condición de 2 ramas
        // (respondió / no respondió). El backend elige cuál seguir al reanudar,
        // según por qué se despertó la corrida -- ver resolveWaitBranch.
        if (n.data.stepType === 'wait' && n.data.config?.mode === 'until_reply') {
          const trueEdge = outgoing.find(e => e.sourceHandle === 'true');
          const falseEdge = outgoing.find(e => e.sourceHandle === 'false');
          const plain = outgoing.find(e => !e.sourceHandle);
          return {
            tempId: n.id, type: n.data.stepType, config: n.data.config,
            position_x: n.position.x, position_y: n.position.y,
            branchTrue: trueEdge?.target ?? null,
            branchFalse: falseEdge?.target ?? null,
            // Si el usuario nunca conectó las ramas nuevas, se conserva la
            // salida única de siempre para no romper el flujo que ya tenía.
            next: (!trueEdge && !falseEdge) ? (plain?.target ?? null) : null
          };
        }
        return { tempId: n.id, type: n.data.stepType, config: n.data.config, position_x: n.position.x, position_y: n.position.y, next: outgoing[0]?.target ?? null };
      });
      const firstEdge = (edgesBySource['trigger'] || [])[0];
      await apiClient.put(`/api/pos/crm/workflows/${workflowId}/steps`, { steps, firstStepTempId: firstEdge?.target ?? null });

      showAlert('success', 'Guardado', 'El flujo se guardó correctamente.');
      await load(); // recarga: los ids temporales de nodos nuevos pasan a ser los ids reales de la DB
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo guardar el flujo.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (dirty) {
      showAlert('warning', 'Guarda primero', 'Tenés cambios sin guardar -- guardalos antes de activar o pausar este workflow.');
      return;
    }
    try {
      await apiClient.put(`/api/pos/crm/workflows/${workflowId}`, { is_active: !workflowMeta.is_active });
      load();
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo cambiar el estado.');
    }
  };

  const openRuns = async () => {
    try {
      const res = await apiClient.get(`/api/pos/crm/workflows/${workflowId}/runs`);
      setRuns(res.data || []);
      setShowRuns(true);
      setExpandedRunId(null);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo cargar el historial.');
    }
  };

  // ⭐ Click en una corrida del Historial (pedido de Angelo, 2026-08-07,
  // estilo GHL: "al dar click en una de las acciones ya ejecutadas, sale la
  // ruta de trabajo"): despliega/colapsa la ruta de esa corrida in-place, sin
  // modal aparte -- así se puede comparar varias corridas sin perder el
  // listado de contexto. Cachea por run.id en `runSteps` para no repetir la
  // petición si el usuario colapsa y vuelve a abrir la misma.
  const toggleRunSteps = async (run) => {
    if (expandedRunId === run.id) { setExpandedRunId(null); return; }
    setExpandedRunId(run.id);
    if (runSteps[run.id]) return; // ya cacheado
    setLoadingRunSteps(true);
    try {
      const res = await apiClient.get(`/api/pos/crm/workflow-runs/${run.id}/steps`);
      setRunSteps(prev => ({ ...prev, [run.id]: res.data || [] }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo cargar la ruta de ejecución de esta corrida.');
      setExpandedRunId(null);
    } finally {
      setLoadingRunSteps(false);
    }
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {customAlert && (
        <CustomAlert type={customAlert.type} title={customAlert.title} message={customAlert.message} onClose={() => setCustomAlert(null)} zIndex={80} />
      )}

      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white gap-3">
        <div className="flex items-center gap-2 min-w-0 group">
          <button onClick={onBack} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><ArrowLeft className="w-4 h-4" /></button>
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveRenameWorkflow}
              onKeyDown={(e) => { if (e.key === 'Enter') saveRenameWorkflow(); if (e.key === 'Escape') setEditingName(false); }}
              maxLength={80}
              className="font-semibold text-gray-800 px-1.5 py-0.5 border border-blue-400 rounded-lg text-sm min-w-0 flex-1"
            />
          ) : (
            <>
              <span className="font-semibold text-gray-800 truncate">{workflowMeta?.name}</span>
              <button onClick={startEditName} className="text-gray-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 flex-shrink-0" title="Renombrar">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {dirty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">Sin guardar</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={openRuns} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100">
            <History className="w-3.5 h-3.5" /> Historial
          </button>
          <button
            onClick={toggleActive}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${workflowMeta?.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            {workflowMeta?.is_active ? 'Activo' : 'Pausado'}
          </button>
          {/* ⭐ Fix (pedido de Angelo, 2026-08-07): antes este botón era
              siempre azul intenso sin importar `dirty`, dando la falsa
              impresión constante de "hay algo sin guardar, apretame".
              Ahora solo se ve azul/activo cuando dirty es true -- sin
              cambios pendientes queda gris/apagado (sigue siendo
              clickeable por si Angelo quiere forzar un guardado igual, no
              hace falta deshabilitarlo de verdad). */}
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ${
              dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
            }`}
          >
            <Save className="w-3.5 h-3.5" /> {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* ⭐ Workflows v2: barra de pasos agrupada por familia
            (STEP_CATEGORIES) en vez de una lista plana -- cada botón trae
            su badge de color a juego con el nodo que va a crear en el
            canvas, para reconocer de un vistazo qué tipo de paso es antes
            de siquiera leer el texto. */}
        <div className="w-48 flex-shrink-0 border-r bg-gray-50/60 p-2.5 space-y-3 overflow-y-auto">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide px-1">Agregar paso</p>
          {STEP_CATEGORIES.map((cat) => {
            const items = cat.types
              .filter((type) => STEP_META[type])
              // ⭐ 'ai_reply'/'ai_intent' solo se ofrecen si la cuenta tiene
              // la IA activada (crm_ai_enabled) -- ver hasAiAccess, cargado
              // en el componente padre. Un workflow guardado que ya tenga
              // estos nodos los sigue mostrando igual en el canvas, solo se
              // oculta la opción de AGREGAR uno nuevo si no califica.
              .filter((type) => (type !== 'ai_reply' && type !== 'ai_intent') || hasAiAccess);
            if (!items.length) return null;
            return (
              <div key={cat.label}>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide px-1 mb-1">{cat.label}</p>
                <div className="space-y-1">
                  {items.map((type) => {
                    const meta = STEP_META[type];
                    const Icon = meta.icon;
                    const colors = STEP_COLOR_CLASSES[meta.color] || STEP_COLOR_CLASSES.gray;
                    return (
                      <button
                        key={type}
                        onClick={() => addStepNode(type)}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-xl text-xs font-semibold text-gray-600 bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-600 hover:shadow-sm transition-all"
                      >
                        <span className={`flex items-center justify-center w-5 h-5 rounded-md ${colors.bg} ${colors.text} flex-shrink-0`}>
                          <Icon className="w-3 h-3" />
                        </span>
                        <span className="truncate">{meta.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              onNodeClick={(_, node) => { setSelectedNodeId(node.id); setNodeMenu(null); }}
              onPaneClick={() => { setSelectedNodeId(null); setNodeMenu(null); }}
              // Click derecho sobre el nodo = mismo menú que el botón "⋯".
              onNodeContextMenu={(e, node) => {
                e.preventDefault();
                if (node.id !== 'trigger') setNodeMenu({ nodeId: node.id, x: e.clientX, y: e.clientY });
              }}
              nodeTypes={nodeTypes}
              // ⭐ Workflows v2: aristas smoothstep con punta de flecha por
              // defecto (antes era la línea recta sin flecha de React Flow)
              // -- las conexiones ya coloreadas/etiquetadas por rama (ver
              // onConnect/load) siguen pisando este default vía su propio
              // `style`, esto solo agrega la forma/flecha común a todas.
              defaultEdgeOptions={{ type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 16, height: 16 } }}
              fitView
              deleteKeyCode={['Backspace', 'Delete']}
            >
              <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="#e2e8f0" />
              <Controls />
              <MiniMap
                pannable
                zoomable
                nodeColor={(n) => (n.data?.kind === 'trigger' ? '#2563eb' : (STEP_COLOR_CLASSES[STEP_META[n.data?.stepType]?.color]?.hex || '#94a3b8'))}
                maskColor="rgba(241,245,249,0.65)"
                className="!bg-white !border !border-gray-200 !rounded-lg"
              />
            </ReactFlow>
          </ReactFlowProvider>

          {/* ⭐ Menú "⋯" del nodo, calcado de GHL (pedido de Angelo, 2026-08-10).
              Va en position:fixed sobre coordenadas de pantalla y NO dentro del
              lienzo: si viviera adentro, el zoom/paneo de React Flow lo
              escalaría y lo movería junto con los nodos. */}
          {nodeMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNodeMenu(null)} />
              <div
                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 w-64 text-sm"
                style={{ left: Math.min(nodeMenu.x, window.innerWidth - 270), top: Math.min(nodeMenu.y, window.innerHeight - 300) }}
              >
                {[
                  { label: 'Copiar acción', fn: () => copyToClipboard(new Set([nodeMenu.nodeId])) },
                  { label: 'Copiar todas las acciones desde aquí', fn: () => copyToClipboard(collectFromHere(nodeMenu.nodeId)) },
                  { sep: true },
                  { label: 'Mover acción', hint: 'quedará seleccionada: arrastrala', fn: () => selectFromHere(nodeMenu.nodeId, true) },
                  { label: 'Mover todas las acciones desde aquí', hint: 'se arrastran en bloque', fn: () => selectFromHere(nodeMenu.nodeId, false) },
                  { sep: true },
                  { label: 'Eliminar acción', danger: true, fn: () => deleteNode(nodeMenu.nodeId) },
                  { label: 'Borrar todas las acciones desde aquí', danger: true, fn: () => deleteFromHere(nodeMenu.nodeId) },
                ].map((item, i) => item.sep ? (
                  <hr key={i} className="my-1 border-gray-100" />
                ) : (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { item.fn(); setNodeMenu(null); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${item.danger ? 'text-red-600' : 'text-gray-700'}`}
                  >
                    {item.label}
                    {item.hint && <span className="block text-[10px] text-gray-400">{item.hint}</span>}
                  </button>
                ))}
                {clipboard && clipboard.nodes.length > 0 && (
                  <>
                    <hr className="my-1 border-gray-100" />
                    <button
                      type="button"
                      onClick={() => { pasteClipboard(); setNodeMenu(null); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 text-indigo-700 font-semibold"
                    >
                      Pegar {clipboard.nodes.length} acción{clipboard.nodes.length === 1 ? '' : 'es'}
                      <span className="block text-[10px] text-indigo-400 font-normal">se pegan sin conectar al flujo</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Panel de configuración del nodo seleccionado */}
        {selectedNode && (
          <div className="w-72 flex-shrink-0 border-l bg-white overflow-y-auto p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-sm text-gray-800">{selectedNode.data.kind === 'trigger' ? 'Disparador' : (STEP_META[selectedNode.data.stepType]?.label || 'Condición')}</h4>
              {selectedNode.data.kind !== 'trigger' && (
                <button onClick={() => deleteNode(selectedNode.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>

            {selectedNode.data.kind === 'trigger' && (
              <>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Cuándo</label>
                  <select value={selectedNode.data.triggerType} onChange={(e) => updateTriggerType(e.target.value)} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                    {Object.entries(TRIGGER_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                {selectedNode.data.triggerType === 'stage_changed' && (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Etapa</label>
                    <select value={selectedNode.data.config.stageId || ''} onChange={(e) => updateNodeConfig('trigger', { stageId: e.target.value ? Number(e.target.value) : null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                      <option value="">Cualquier etapa</option>
                      {pipelineStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {selectedNode.data.triggerType === 'tag_added' && (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Etiqueta</label>
                    <select value={selectedNode.data.config.tagId || ''} onChange={(e) => updateNodeConfig('trigger', { tagId: e.target.value ? Number(e.target.value) : null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                      <option value="">Cualquier etiqueta</option>
                      {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                {selectedNode.data.triggerType === 'message_received' && (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Canal de WhatsApp</label>
                    <select value={selectedNode.data.config.channelId || ''} onChange={(e) => updateNodeConfig('trigger', { channelId: e.target.value ? Number(e.target.value) : null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                      <option value="">Cualquier canal</option>
                      {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-1">Este workflow solo se activa cuando el mensaje entra por ese número/canal.</p>
                  </div>
                )}
                {selectedNode.data.triggerType === 'field_changed' && (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Campo que debe cambiar</label>
                    <select value={selectedNode.data.config.field || ''} onChange={(e) => updateNodeConfig('trigger', { field: e.target.value || null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                      <option value="">Cualquier campo</option>
                      <option value="sector">Sector</option><option value="birthday">Cumpleaños</option><option value="name">Nombre</option><option value="email">Correo</option>
                      {customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}
                    </select>
                    <input value={selectedNode.data.config.value || ''} onChange={(e) => updateNodeConfig('trigger', { value: e.target.value })} placeholder="Valor opcional, ej. Carapungo" className="w-full mt-1.5 px-2 py-1.5 border rounded-lg text-sm" />
                  </div>
                )}
              </>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'send_message' && (
              <>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => updateNodeConfig(selectedNode.id, { templateId: null })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${!selectedNode.data.config.templateId ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Texto libre
                  </button>
                  <button
                    onClick={() => updateNodeConfig(selectedNode.id, { templateId: templates[0]?.id || null, content: null })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${selectedNode.data.config.templateId ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Plantilla
                  </button>
                </div>
                {selectedNode.data.config.templateId ? (
                  templates.length === 0 ? (
                    <p className="text-xs text-gray-500">Todavía no tenés plantillas. <button onClick={onManageTemplates} className="text-blue-600 underline">Crear una</button></p>
                  ) : (
                    <select value={selectedNode.data.config.templateId || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { templateId: Number(e.target.value) })} className="w-full px-2 py-1.5 border rounded-lg text-sm">
                      {templates.map(t => <option key={t.id} value={t.id}>{t.has_media ? '📎 ' : ''}{t.name}</option>)}
                    </select>
                  )
                ) : (
                  <div className="relative">
                    <textarea
                      ref={sendMessageContentRef}
                      value={selectedNode.data.config.content || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { content: e.target.value })}
                      placeholder="Hola {{contact.name}}, ..."
                      rows={4}
                      className="w-full px-2 py-1.5 pr-7 border rounded-lg text-sm"
                    />
                    <div className="absolute top-1.5 right-1.5">
                      <CustomValueButton
                        textareaRef={sendMessageContentRef}
                        value={selectedNode.data.config.content || ''}
                        onChange={(v) => updateNodeConfig(selectedNode.id, { content: v })}
                      />
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-gray-400">Podés usar <code>{'{{contact.name}}'}</code>, <code>{'{{contact.phone}}'}</code>, etc. -- botón <Braces className="w-2.5 h-2.5 inline" /> para ver todos.</p>
                <TestSendBox
                  templateId={selectedNode.data.config.templateId}
                  content={selectedNode.data.config.content}
                />
              </>
            )}

            {/* ⭐ Aviso de rama sin conectar (2026-08-09). Un nodo de espera con
                una salida vacía NO es un error del sistema: el flujo llega ahí,
                no encuentra próximo paso y se marca completado. Visto desde el
                Historial parecía que el workflow "moría", así que el editor
                ahora lo dice ANTES de que pase. */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'wait'
              && selectedNode.data.config?.mode === 'until_reply'
              && (() => {
                const out = edges.filter(e => e.source === selectedNode.id);
                const missing = [];
                if (!out.some(e => e.sourceHandle === 'true')) missing.push('Respondió');
                if (!out.some(e => e.sourceHandle === 'false')) missing.push('No respondió');
                if (!missing.length) return null;
                return (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                    ⚠️ {missing.length === 2 ? 'Ninguna salida está conectada' : `La salida "${missing[0]}" no está conectada`}.
                    {' '}Si la corrida llega por ahí, el flujo termina en este nodo sin ejecutar nada más.
                    {' '}Conectá esa patita a un paso, o dejala vacía a propósito si querés que el flujo corte ahí.
                  </p>
                );
              })()}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'wait' && (
              <div className="space-y-3">
                {/* ⭐ Selector de modo (pedido de Angelo, 2026-08-07: replicar
                    el action "Wait" de GHL) -- sin mode guardado = 'duration',
                    así los workflows viejos con este paso se siguen viendo y
                    comportando exactamente igual que antes. */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tipo de espera</label>
                  <div className="flex gap-1.5 mt-1">
                    <button
                      onClick={() => updateNodeConfig(selectedNode.id, { mode: 'duration' })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${(!selectedNode.data.config.mode || selectedNode.data.config.mode === 'duration') ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      Tiempo fijo
                    </button>
                    <button
                      onClick={() => updateNodeConfig(selectedNode.id, { mode: 'until_reply' })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${selectedNode.data.config.mode === 'until_reply' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      Hasta que responda
                    </button>
                  </div>
                </div>

                {(!selectedNode.data.config.mode || selectedNode.data.config.mode === 'duration') && (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Esperar (minutos)</label>
                    <input
                      type="number"
                      min={1}
                      value={selectedNode.data.config.minutes || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { minutes: Number(e.target.value) || 0 })}
                      className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                    />
                    <div className="flex gap-1 mt-2">
                      {[{ l: '5 min', v: 5 }, { l: '1 hora', v: 60 }, { l: '1 día', v: 1440 }, { l: '3 días', v: 4320 }].map(opt => (
                        <button key={opt.v} onClick={() => updateNodeConfig(selectedNode.id, { minutes: opt.v })} className="flex-1 text-[10px] py-1 rounded border text-gray-500 hover:border-blue-400 hover:text-blue-600">{opt.l}</button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedNode.data.config.mode === 'until_reply' && (
                  <>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Responder a</label>
                      <div className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm bg-gray-50 text-gray-500">WhatsApp</div>
                      <p className="text-[10px] text-gray-400 mt-1">Único canal disponible por ahora.</p>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.config.timeoutEnabled !== false}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { timeoutEnabled: e.target.checked })}
                      />
                      Tiempo de espera agotado (timeout)
                    </label>
                    {selectedNode.data.config.timeoutEnabled !== false ? (
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          min={0.1}
                          step={0.1}
                          value={selectedNode.data.config.timeoutValue ?? 30}
                          onChange={(e) => updateNodeConfig(selectedNode.id, { timeoutValue: Number(e.target.value) || 0 })}
                          className="w-20 px-2 py-1.5 border rounded-lg text-sm"
                        />
                        <select
                          value={selectedNode.data.config.timeoutUnit || 'minutes'}
                          onChange={(e) => updateNodeConfig(selectedNode.id, { timeoutUnit: e.target.value })}
                          className="flex-1 px-2 py-1.5 border rounded-lg text-sm"
                        >
                          <option value="minutes">Minutos</option>
                          <option value="hours">Horas</option>
                          <option value="days">Días</option>
                        </select>
                      </div>
                    ) : (
                      <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                        Sin timeout, el flujo espera como máximo 7 días (tope de seguridad) -- nunca queda esperando para siempre.
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400">Si el contacto responde antes, el flujo sigue de inmediato. Si se agota el tiempo, también sigue.</p>
                  </>
                )}
              </div>
            )}

            {selectedNode.data.kind === 'step' && (selectedNode.data.stepType === 'add_tag' || selectedNode.data.stepType === 'remove_tag') && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Etiqueta</label>
                <select value={selectedNode.data.config.tagId || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { tagId: Number(e.target.value) || null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                  <option value="">Elegir...</option>
                  {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'set_stage' && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Pipeline</label>
                <select value={selectedNode.data.config.pipelineId || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { pipelineId: e.target.value ? Number(e.target.value) : null, stageId: null })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                  <option value="">Selecciona un pipeline...</option>
                  {(pipelines || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Nueva etapa</label>
                <select
                  value={selectedNode.data.config.stageId ?? ''}
                  onChange={(e) => updateNodeConfig(selectedNode.id, { stageId: e.target.value ? Number(e.target.value) : null })}
                  className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                >
                  <option value="">Sin etapa</option>
                  {pipelineStages.filter(s => !selectedNode.data.config.pipelineId || String(s.pipeline_id) === String(selectedNode.data.config.pipelineId)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'set_temperature' && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Nueva temperatura</label>
                <select
                  value={selectedNode.data.config.temperature || ''}
                  onChange={(e) => updateNodeConfig(selectedNode.id, { temperature: e.target.value || null })}
                  className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                >
                  <option value="">Elegir...</option>
                  {Object.entries(TEMP_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </div>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'condition' && (
              <>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Si el contacto...</label>
                  <select
                    value={selectedNode.data.config.field || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { field: e.target.value, value: null, matchType: e.target.value === 'message' ? 'contains' : undefined, branches: e.target.value === 'message_type' ? (selectedNode.data.config.branches || []) : undefined })}
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="">Elegir...</option>
                    <option value="stage">está en la etapa</option>
                    <option value="tag">tiene la etiqueta</option>
                    <option value="temperature">tiene la temperatura</option>
                    <option value="message">escribió un mensaje que...</option>
                    <option value="message_type">escribió un mensaje de tipo...</option>
                  </select>
                </div>
                {selectedNode.data.config.field === 'message_type' && (
                  <>
                    <p className="text-[10px] text-gray-400">
                      Elegí uno o varios tipos. Cada tipo activado es una salida propia del nodo -- conectala al paso que corresponda. Los mensajes que no calcen con ninguno salen por "otro".
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {MESSAGE_TYPE_BRANCH_META.map((m) => {
                        const active = Array.isArray(selectedNode.data.config.branches) && selectedNode.data.config.branches.includes(m.key);
                        return (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => {
                              const current = Array.isArray(selectedNode.data.config.branches) ? selectedNode.data.config.branches : [];
                              const next = active ? current.filter((k) => k !== m.key) : [...current, m.key];
                              updateNodeConfig(selectedNode.id, { branches: next });
                            }}
                            className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                              active ? 'bg-yellow-100 border-yellow-400 text-yellow-800' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            {m.emoji} {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                {selectedNode.data.config.field === 'message' && (
                  <>
                    <select
                      value={selectedNode.data.config.matchType || 'contains'}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { matchType: e.target.value })}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    >
                      <option value="contains">Contiene</option>
                      <option value="not_contains">No contiene</option>
                      <option value="equals">Es exactamente</option>
                      <option value="starts_with">Empieza con</option>
                    </select>
                    <input
                      type="text"
                      placeholder='Palabra o frase, ej: "precio"'
                      value={selectedNode.data.config.value || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { value: e.target.value })}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    />
                    <p className="text-[10px] text-gray-400">
                      Evalúa el último mensaje que el contacto escribió por WhatsApp. No distingue mayúsculas de minúsculas.
                    </p>
                  </>
                )}
                {selectedNode.data.config.field === 'stage' && (
                  <select value={selectedNode.data.config.value || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { value: Number(e.target.value) || null })} className="w-full px-2 py-1.5 border rounded-lg text-sm">
                    <option value="">Elegir etapa...</option>
                    {pipelineStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                {selectedNode.data.config.field === 'tag' && (
                  <select value={selectedNode.data.config.value || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { value: Number(e.target.value) || null })} className="w-full px-2 py-1.5 border rounded-lg text-sm">
                    <option value="">Elegir etiqueta...</option>
                    {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                {selectedNode.data.config.field === 'temperature' && (
                  <select value={selectedNode.data.config.value || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { value: e.target.value || null })} className="w-full px-2 py-1.5 border rounded-lg text-sm">
                    <option value="">Elegir...</option>
                    {Object.entries(TEMP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                )}
                {selectedNode.data.config.field !== 'message_type' && (
                  <p className="text-[10px] text-gray-400">Conectá la salida "Sí" y la salida "No" del nodo a los pasos que correspondan.</p>
                )}
              </>
            )}

            {/* ⭐ Workflows v2 (2026-08-09) -- 'Rama múltiple': reemplazo
                generalizado de 'Condición' con N salidas de nombre libre,
                en modo 'reglas' (AND/OR por rama) o 'Split A/B' (sorteo
                ponderado por %, sin mirar nada del contacto). Ver
                evaluateBranchNode en workflowEngine.js. */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'branch' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🔀 Reemplazo del nodo "Condición" clásico: definí tantas ramas como necesites, cada una con su propio nombre -- conectá cada una al paso que corresponda. Los contactos que no matcheen ninguna rama salen por "sin match".
                </p>

                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => updateNodeConfig(selectedNode.id, { mode: 'rules' })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${(selectedNode.data.config.mode || 'rules') === 'rules' ? 'bg-indigo-100 border-indigo-400 text-indigo-800' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                  >
                    Por reglas
                  </button>
                  <button
                    type="button"
                    onClick={() => updateNodeConfig(selectedNode.id, { mode: 'random' })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${selectedNode.data.config.mode === 'random' ? 'bg-indigo-100 border-indigo-400 text-indigo-800' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                  >
                    Split A/B (%)
                  </button>
                </div>

                {(Array.isArray(selectedNode.data.config.branches) ? selectedNode.data.config.branches : []).map((branch, bi) => {
                  const branches = selectedNode.data.config.branches;
                  const updateBranch = (patch) => updateNodeConfig(selectedNode.id, { branches: branches.map((b, i) => (i === bi ? { ...b, ...patch } : b)) });
                  const removeBranch = () => updateNodeConfig(selectedNode.id, { branches: branches.filter((_, i) => i !== bi) });
                  return (
                    <div key={branch.key} className="border border-indigo-200 rounded-lg p-2 space-y-1.5 bg-indigo-50/40">
                      <div className="flex items-center gap-1.5">
                        <input type="text" value={branch.label || ''} onChange={(e) => updateBranch({ label: e.target.value })} placeholder="Nombre de la rama" className="flex-1 px-2 py-1 border rounded text-xs" />
                        <button type="button" onClick={removeBranch} className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                      </div>

                      {selectedNode.data.config.mode === 'random' ? (
                        <div className="flex items-center gap-1.5">
                          <input type="number" min={0} max={100} value={branch.weight ?? 0} onChange={(e) => updateBranch({ weight: Number(e.target.value) || 0 })} className="w-16 px-2 py-1 border rounded text-xs" />
                          <span className="text-[10px] text-gray-500">% de probabilidad</span>
                        </div>
                      ) : (
                        <>
                          {/* ⭐ Aviso (2026-08-10). Varias palabras clave unidas por
                              "Y (todas)" exigen que el mensaje las contenga A TODAS
                              -- casi nunca es lo que se quiere: un cliente escribe
                              "peluqueria" O "grooming", no las dos juntas. Es el
                              error más fácil de cometer acá y el más difícil de ver,
                              porque a simple vista la palabra "está". */}
                          {Array.isArray(branch.rules)
                            && branch.rules.filter((r) => r.field === 'message').length > 1
                            && branch.rules.slice(1).every((r) => (r.join || branch.logic || 'AND') === 'AND') && (
                            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 leading-tight">
                              ⚠️ Con <b>AND</b> el mensaje tiene que contener <b>todas</b> estas palabras a la vez.
                              Si son sinónimos (peluquería / grooming), cambiá el conector a <b>OR</b>.
                            </p>
                          )}
                          {(Array.isArray(branch.rules) ? branch.rules : []).map((rule, ri) => {
                            const updateRule = (patch) => updateBranch({ rules: branch.rules.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
                            const removeRule = () => updateBranch({ rules: branch.rules.filter((_, i) => i !== ri) });
                            return (
                              <React.Fragment key={ri}>
                                {/* ⭐ Conector AND/OR ENTRE condiciones, estilo GHL
                                    (pedido de Angelo, 2026-08-10). Antes eran dos
                                    botones arriba de la lista: se leía como un
                                    filtro del panel, no como el "y/o" que une DOS
                                    reglas concretas. Acá cae justo entre las dos
                                    que une, que es lo que lo vuelve obvio. */}
                                {ri > 0 && (
                                  <div className="flex items-center gap-2 py-0.5">
                                    {/* El conector vive en la regla de la DERECHA
                                        (rule.join) -- así cada hueco es independiente
                                        y se puede mezclar AND y OR en la misma rama. */}
                                    <select
                                      value={rule.join || branch.logic || 'AND'}
                                      onChange={(e) => updateRule({ join: e.target.value })}
                                      className="px-1.5 py-0.5 border border-indigo-300 rounded text-[10px] font-semibold text-indigo-700 bg-white"
                                    >
                                      <option value="AND">AND</option>
                                      <option value="OR">OR</option>
                                    </select>
                                    <span className="flex-1 border-t border-dashed border-indigo-200" />
                                  </div>
                                )}
                              <div className="bg-white border border-gray-200 rounded p-1.5 space-y-1">
                                <div className="flex items-center gap-1">
                                  <select
                                    value={rule.field || ''}
                                    onChange={(e) => updateRule({ field: e.target.value, value: null, matchType: e.target.value === 'message' ? 'contains' : undefined })}
                                    className="flex-1 px-1.5 py-1 border rounded text-[11px]"
                                  >
                                    <option value="">Campo...</option>
                                    <option value="stage">Etapa</option>
                                    <option value="tag">Etiqueta</option>
                                    <option value="temperature">Temperatura</option>
                                    <option value="message">Mensaje contiene...</option>
                                    <option value="message_type">Tipo de mensaje</option>
                                    {customFields.length > 0 && <optgroup label="Campos personalizados">{customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}</optgroup>}
                                  </select>
                                  <button type="button" onClick={removeRule} className="text-gray-300 hover:text-red-600"><X className="w-3 h-3" /></button>
                                </div>
                                {rule.field === 'stage' && (
                                  <select value={rule.value || ''} onChange={(e) => updateRule({ value: Number(e.target.value) || null })} className="w-full px-1.5 py-1 border rounded text-[11px]">
                                    <option value="">Elegir etapa...</option>
                                    {pipelineStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                  </select>
                                )}
                                {rule.field === 'tag' && (
                                  <select value={rule.value || ''} onChange={(e) => updateRule({ value: Number(e.target.value) || null })} className="w-full px-1.5 py-1 border rounded text-[11px]">
                                    <option value="">Elegir etiqueta...</option>
                                    {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                  </select>
                                )}
                                {rule.field === 'temperature' && (
                                  <select value={rule.value || ''} onChange={(e) => updateRule({ value: e.target.value || null })} className="w-full px-1.5 py-1 border rounded text-[11px]">
                                    <option value="">Elegir...</option>
                                    {Object.entries(TEMP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                  </select>
                                )}
                                {rule.field === 'message' && (
                                  <div className="flex gap-1">
                                    <select value={rule.matchType || 'contains'} onChange={(e) => updateRule({ matchType: e.target.value })} className="px-1.5 py-1 border rounded text-[11px]">
                                      <option value="contains">Contiene</option>
                                      <option value="not_contains">No contiene</option>
                                      <option value="equals">Es exactamente</option>
                                      <option value="starts_with">Empieza con</option>
                                    </select>
                                    <input type="text" value={rule.value || ''} onChange={(e) => updateRule({ value: e.target.value })} placeholder="palabra/frase" className="flex-1 px-1.5 py-1 border rounded text-[11px]" />
                                  </div>
                                )}
                                {rule.field === 'message_type' && (
                                  <select value={rule.value || ''} onChange={(e) => updateRule({ value: e.target.value || null })} className="w-full px-1.5 py-1 border rounded text-[11px]">
                                    <option value="">Elegir tipo...</option>
                                    {MESSAGE_TYPE_BRANCH_META.map(m => <option key={m.key} value={m.key}>{m.emoji} {m.label}</option>)}
                                  </select>
                                )}
                                {String(rule.field || '').startsWith('custom:') && (
                                  <input type="text" value={rule.value || ''} onChange={(e) => updateRule({ value: e.target.value })} placeholder="Valor del campo" className="w-full px-1.5 py-1 border rounded text-[11px]" />
                                )}
                              </div>
                              </React.Fragment>
                            );
                          })}
                          {/* ⭐ Fila inferior estilo GHL: el conector + el botón de
                              agregar, juntos. Así se elige el AND/OR ANTES de sumar
                              la segunda condición, en vez de descubrirlo después. */}
                          <div className="flex items-center gap-1.5 pt-0.5">
                            {/* `nextJoin` es solo del editor: con qué conector se
                                sumará la PRÓXIMA condición. Igual que en GHL, se
                                elige antes de apretar "+". */}
                            <select
                              value={branch.nextJoin || 'AND'}
                              onChange={(e) => updateBranch({ nextJoin: e.target.value })}
                              className="px-1.5 py-0.5 border border-indigo-300 rounded text-[10px] font-semibold text-indigo-700 bg-white"
                            >
                              <option value="AND">AND</option>
                              <option value="OR">OR</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => updateBranch({
                                rules: [...(Array.isArray(branch.rules) ? branch.rules : []), { field: '', join: branch.nextJoin || 'AND' }],
                              })}
                              title="Agregar condición a esta rama"
                              className="w-6 h-6 flex items-center justify-center rounded border border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-bold text-sm leading-none"
                            >
                              +
                            </button>
                            <span className="text-[10px] text-gray-400">
                              agregar condición con {branch.nextJoin || 'AND'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => {
                    const branches = Array.isArray(selectedNode.data.config.branches) ? selectedNode.data.config.branches : [];
                    const key = slugifyBranchKey(`rama_${branches.length + 1}`, branches.map(b => b.key));
                    updateNodeConfig(selectedNode.id, { branches: [...branches, { key, label: `Rama ${branches.length + 1}`, logic: 'AND', rules: [], weight: 0 }] });
                  }}
                  className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 border border-dashed border-indigo-300 hover:bg-indigo-50"
                >
                  <Plus className="w-3.5 h-3.5" /> Agregar rama
                </button>
                <p className="text-[10px] text-gray-400">Conectá cada rama (y la salida "sin match") a los pasos que correspondan en el canvas.</p>
              </>
            )}

            {/* ⭐ Workflows v2 -- 'Horario laboral': 2 salidas fijas
                (Dentro/Fuera), horario semanal en base a la hora de Ecuador
                (UTC-5 fijo). Ver evaluateBusinessHours en workflowEngine.js. */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'business_hours' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🕐 2 salidas fijas: "Dentro" y "Fuera" de horario, según la hora actual en Ecuador. Un día sin ningún rango marcado cuenta siempre como "Fuera" para ese día.
                </p>
                {WEEKDAY_LABELS.map((day, idx) => {
                  const ranges = (selectedNode.data.config.schedule && selectedNode.data.config.schedule[idx]) || [];
                  const updateDay = (nextRanges) => updateNodeConfig(selectedNode.id, { schedule: { ...(selectedNode.data.config.schedule || {}), [idx]: nextRanges } });
                  return (
                    <div key={idx} className="border border-teal-200 rounded-lg p-2 space-y-1">
                      <p className="text-[11px] font-bold text-teal-700">{day}</p>
                      {ranges.map((r, ri) => (
                        <div key={ri} className="flex items-center gap-1">
                          <input type="time" value={r.start || ''} onChange={(e) => updateDay(ranges.map((x, i) => (i === ri ? { ...x, start: e.target.value } : x)))} className="px-1.5 py-1 border rounded text-[11px]" />
                          <span className="text-[10px] text-gray-400">a</span>
                          <input type="time" value={r.end || ''} onChange={(e) => updateDay(ranges.map((x, i) => (i === ri ? { ...x, end: e.target.value } : x)))} className="px-1.5 py-1 border rounded text-[11px]" />
                          <button type="button" onClick={() => updateDay(ranges.filter((_, i) => i !== ri))} className="text-gray-300 hover:text-red-600"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => updateDay([...ranges, { start: '09:00', end: '18:00' }])} className="text-[10px] font-semibold text-teal-600 hover:text-teal-800">+ Agregar rango</button>
                    </div>
                  );
                })}
              </>
            )}

            {/* ⭐ Workflows v2 -- 'Actualizar campo': whitelist fija de
                columnas escribibles, ver UPDATABLE_CONTACT_FIELDS en
                workflowEngine.js (backend rechaza cualquier otra). */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'update_field' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  ✏️ Escribe un dato del contacto directamente, sin depender de que el cliente lo escriba en el chat -- útil después de "Detectar intención (IA)" o de una condición que ya dedujo el dato.
                </p>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Campo a actualizar</label>
                  <select value={selectedNode.data.config.field || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { field: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm">
                    <option value="">Elegir...</option>
                    <option value="name">Nombre</option>
                    <option value="email">Email</option>
                    <option value="cedula">Cédula</option>
                    <option value="sector">Sector</option>
                    <option value="birthday">Cumpleaños</option>
                    {customFields.length > 0 && <optgroup label="Campos personalizados">{customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}</optgroup>}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Nuevo valor</label>
                    <CustomValueButton textareaRef={updateFieldValueRef} value={selectedNode.data.config.value || ''} onChange={(v) => updateNodeConfig(selectedNode.id, { value: v })} />
                  </div>
                  <input
                    ref={updateFieldValueRef}
                    type="text"
                    value={selectedNode.data.config.value || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { value: e.target.value })}
                    placeholder='Ej: "{{contact.name}} VIP"'
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  />
                </div>
              </>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'ai_extract' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border rounded-lg p-2">Elige qué datos quieres detectar. La IA revisará la conversación y solo guardará lo que el cliente haya dicho claramente.</p>
                {(Array.isArray(selectedNode.data.config.targets) ? selectedNode.data.config.targets : []).map((target, ti) => {
                  const targets = selectedNode.data.config.targets || [];
                  const updateTarget = (patch) => updateNodeConfig(selectedNode.id, { targets: targets.map((item, i) => i === ti ? { ...item, ...patch } : item) });
                  return <div key={`${target.field}-${ti}`} className="border border-purple-200 rounded-lg p-2 space-y-1.5 bg-purple-50/40">
                    <div className="flex items-center gap-1.5"><select value={target.field || ''} onChange={e => updateTarget({ field: e.target.value })} className="flex-1 px-2 py-1.5 border rounded text-xs"><option value="">Selecciona un campo...</option><optgroup label="Campos básicos"><option value="name">Nombre</option><option value="email">Correo</option><option value="cedula">Cédula</option><option value="sector">Sector</option><option value="birthday">Cumpleaños</option></optgroup>{customFields.length > 0 && <optgroup label="Campos personalizados">{customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}</optgroup>}</select><button type="button" onClick={() => updateNodeConfig(selectedNode.id, { targets: targets.filter((_, i) => i !== ti) })} className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button></div>
                    <input type="text" value={target.instruction || ''} onChange={e => updateTarget({ instruction: e.target.value })} placeholder="¿Qué debe buscar la IA? Ej: cuántos hijos tiene" className="w-full px-2 py-1.5 border rounded text-[11px]" />
                  </div>;
                })}
                <button type="button" onClick={() => updateNodeConfig(selectedNode.id, { targets: [...(selectedNode.data.config.targets || []), { field: '', instruction: '' }] })} className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold text-purple-600 border border-dashed border-purple-300 hover:bg-purple-50"><Plus className="w-3.5 h-3.5" /> Agregar dato a extraer</button>
                    {locations.length > 0 && <div className="border-t pt-2 mt-2 space-y-2"><div><p className="text-[10px] font-bold text-gray-500 uppercase">Resolver sede automáticamente</p><p className="text-[10px] text-gray-400">Toma la zona extraída o ya guardada y la busca en tu tabla de sedes.</p></div><div><label className="text-[10px] font-semibold text-gray-500">Campo de origen</label><select value={selectedNode.data.config.locationSourceField || ''} onChange={e => updateNodeConfig(selectedNode.id, { locationSourceField: e.target.value })} className="w-full px-2 py-1.5 border rounded text-xs"><option value="">Selecciona el campo que contiene la zona...</option><option value="sector">Sector</option>{customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}</select></div><div><label className="text-[10px] font-semibold text-gray-500">Campo de destino</label><select value={selectedNode.data.config.resolveLocationInto || ''} onChange={e => updateNodeConfig(selectedNode.id, { resolveLocationInto: e.target.value })} className="w-full px-2 py-1.5 border rounded text-xs"><option value="">Selecciona dónde guardar la sede...</option><option value="sector">Sector</option>{customFields.map(field => <option key={field.field_key} value={`custom:${field.field_key}`}>{field.label}</option>)}</select></div><p className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded p-1.5">Ejemplo: Sector → Sede. La sede sale de tu tabla; la IA no inventa una sucursal.</p></div>}
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-2 block">Historial a leer</label>
                <input type="number" min="3" max="30" value={selectedNode.data.config.historyLimit || 15} onChange={(e) => updateNodeConfig(selectedNode.id, { historyLimit: Number(e.target.value) || 15 })} className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm" />
              </>
            )}

            {/* ⭐ Workflows v2 -- 'Fin / Meta': nodo terminal, sin salida. */}
            {/* ⭐ Llamar a workflow (2026-08-09). Solo lista los flujos cuyo
                disparador es "Llamado desde otro workflow": el backend rechaza
                cualquier otro, así que ofrecerlos acá sería prometer algo que
                no va a funcionar. */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'call_workflow' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  📞 Arranca otro workflow para el mismo contacto y sigue con el paso siguiente sin esperarlo.
                  El flujo destino tiene que tener el disparador <b>"Llamado desde otro workflow"</b>.
                  Sirve para armar sub-flujos reutilizables y llamarlos desde varios lados sin duplicarlos.
                </p>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Workflow a llamar</label>
                  <select
                    value={selectedNode.data.config.workflowId || ''}
                    onChange={(e) => {
                      const id = Number(e.target.value) || null;
                      const wf = (allWorkflows || []).find(w => w.id === id);
                      // Se guarda también el nombre para que el nodo lo muestre
                      // sin tener que resolverlo contra la lista cada vez.
                      updateNodeConfig(selectedNode.id, { workflowId: id, workflowName: wf?.name || '' });
                    }}
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="">— elegir —</option>
                    {(allWorkflows || [])
                      .filter(w => w.trigger_type === 'workflow_called' && w.id !== workflowId)
                      .map(w => (
                        <option key={w.id} value={w.id}>
                          {w.name}{w.is_active ? '' : ' (pausado)'}
                        </option>
                      ))}
                  </select>
                  {(allWorkflows || []).filter(w => w.trigger_type === 'workflow_called' && w.id !== workflowId).length === 0 && (
                    <p className="text-[10px] text-amber-600 mt-1">
                      No hay flujos invocables todavía. Creá uno con el disparador "Llamado desde otro workflow"
                      y va a aparecer acá.
                    </p>
                  )}
                </div>
              </>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'end_goal' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🏁 Nodo terminal: no tiene salida. Ponele un nombre claro (ej. "Agendó cita", "Se dio de baja") para que el Historial muestre en qué terminó cada corrida, no solo "completado" a secas.
                </p>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre de la meta</label>
                  <input
                    type="text"
                    value={selectedNode.data.config.goal || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { goal: e.target.value })}
                    maxLength={60}
                    placeholder='Ej: "Agendó cita"'
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  />
                </div>
              </>
            )}

            {/* ⭐ Workflows v2 -- 'Detectar intención (IA)': mismo mecanismo
                de N-ramas que 'branch' (getBranchSlots/config.branchTargets),
                pero clasificando con 1 sola llamada a IA en vez de reglas
                manuales. Comparte gate/tope diario con "Responder con IA",
                ver classifyIntent en aiReply.js. */}
            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'ai_intent' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  ✨ Una sola llamada a IA clasifica el último mensaje del contacto contra las intenciones que definas abajo, y rama según cuál matcheó -- usalo en vez de una Condición por palabra clave cuando el cliente puede escribir lo mismo de muchas formas distintas.
                </p>
                {(Array.isArray(selectedNode.data.config.intents) ? selectedNode.data.config.intents : []).map((intent, ii) => {
                  const intents = selectedNode.data.config.intents;
                  const updateIntent = (patch) => updateNodeConfig(selectedNode.id, { intents: intents.map((it, i) => (i === ii ? { ...it, ...patch } : it)) });
                  const removeIntent = () => updateNodeConfig(selectedNode.id, { intents: intents.filter((_, i) => i !== ii) });
                  return (
                    <div key={intent.key} className="border border-purple-200 rounded-lg p-2 space-y-1 bg-purple-50/40">
                      <div className="flex items-center gap-1.5">
                        <input type="text" value={intent.name || ''} onChange={(e) => updateIntent({ name: e.target.value })} placeholder='Ej: "Quiere comprar"' className="flex-1 px-2 py-1 border rounded text-xs" />
                        <button type="button" onClick={removeIntent} className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <input type="text" value={intent.description || ''} onChange={(e) => updateIntent({ description: e.target.value })} placeholder="Pista para la IA (opcional), ej: pregunta precios o quiere agendar" className="w-full px-2 py-1 border rounded text-[11px]" />
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    const intents = Array.isArray(selectedNode.data.config.intents) ? selectedNode.data.config.intents : [];
                    const key = slugifyBranchKey(`intencion_${intents.length + 1}`, intents.map(i => i.key));
                    updateNodeConfig(selectedNode.id, { intents: [...intents, { key, name: `Intención ${intents.length + 1}`, description: '' }] });
                  }}
                  className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold text-purple-600 border border-dashed border-purple-300 hover:bg-purple-50"
                >
                  <Plus className="w-3.5 h-3.5" /> Agregar intención
                </button>
                <p className="text-[10px] text-gray-400">Conectá cada intención (y la salida "ninguna") a los pasos que correspondan.</p>
                {aiUsage && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 flex items-center justify-between text-xs">
                    <span className="text-purple-700 font-medium">Mensajes de IA usados hoy</span>
                    <span className="font-bold text-purple-800">{aiUsage.usedToday} / {Number(aiUsage.dailyLimit) === 0 ? 'ilimitado' : aiUsage.dailyLimit}</span>
                  </div>
                )}
              </>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'ai_reply' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🤖 Usa el proveedor de IA configurado para esta cuenta (api key, URL y modelo se cambian desde el panel de administración -- cualquier proveedor compatible con OpenAI funciona, no solo uno).
                </p>
                {/* ⭐ Transcripción de audio (pedido de Angelo, 2026-08-07):
                    si el último mensaje del contacto fue una nota de voz,
                    este nodo la transcribe primero y responde en base a
                    ESE texto. ⭐ Respuesta en audio (2026-08-08): si además
                    el proveedor configurado es OpenAI, la respuesta se
                    manda como nota de voz real -- con cualquier otro
                    proveedor (DeepSeek incluido a propósito, pedido
                    explícito de Angelo) responde en texto igual que
                    siempre. */}
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🎤 Si el último mensaje del contacto es un audio, este nodo lo transcribe primero (queda guardado en el hilo de la conversación) y responde en base a eso. Si el proveedor de esta cuenta es OpenAI, la respuesta se manda como nota de voz real -- con otro proveedor (ej. DeepSeek) responde en texto.
                </p>
                {/* ⭐ Workflows v2 (2026-08-09, pedido de Angelo): evita que
                    la IA le siga hablando a un cliente que un agente humano
                    ya atendió a mano desde Conversaciones -- chequea si hubo
                    un mensaje saliente con sender_type='agent' DESPUÉS del
                    último mensaje entrante del contacto (ver runAiStep en
                    aiReply.js). Si detecta que ya lo atendieron, este paso
                    no escribe nada y sigue al siguiente nodo con
                    skipped:true en el Historial -- no cuenta como error. */}
                <label className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selectedNode.data.config.pauseIfHumanReplied}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { pauseIfHumanReplied: e.target.checked })}
                  />
                  <span className="text-[11px] text-amber-800 flex items-center gap-1">
                    <UserCheck className="w-3.5 h-3.5" /> No responder si ya contestó un agente humano
                  </span>
                </label>
                {/* ⭐ Audio de prueba (pedido de Angelo, 2026-08-08): probar
                    el chat es un ping de texto, pero el TTS solo se
                    confirma escuchando una nota de voz real en WhatsApp. */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 space-y-1.5">
                  <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Probar respuesta en audio</p>
                  {connectedChannels.length === 0 ? (
                    <p className="text-[10px] text-gray-500">No tenés ningún canal de WhatsApp conectado ahora mismo -- conectá uno desde Conversaciones para poder probar.</p>
                  ) : (
                    <>
                      <select
                        value={testAudioChannelId}
                        onChange={(e) => { setTestAudioChannelId(e.target.value); setTestAudioResult(null); }}
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      >
                        <option value="">Elegí un canal...</option>
                        {connectedChannels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input
                        type="text"
                        value={testAudioPhone}
                        onChange={(e) => { setTestAudioPhone(e.target.value); setTestAudioResult(null); }}
                        placeholder="Número destino, ej: 593987654321"
                        className="w-full px-2 py-1.5 border rounded-lg text-xs"
                      />
                      <button
                        type="button"
                        onClick={handleTestAudio}
                        disabled={testingAudio || !testAudioChannelId || !testAudioPhone.trim()}
                        className="w-full py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {testingAudio ? 'Enviando...' : 'Enviar audio de prueba'}
                      </button>
                      {testAudioResult?.ok && (
                        <p className="text-[10px] text-green-700">✅ Audio enviado -- revisá WhatsApp en ese número.</p>
                      )}
                      {testAudioResult && !testAudioResult.ok && (
                        <p className="text-[10px] text-red-600">❌ {testAudioResult.error}</p>
                      )}
                    </>
                  )}
                </div>
                {/* ⭐ Contador de uso diario (movido acá desde Configuración →
                    IA CRM, que se eliminó -- pedido de Angelo, 2026-08-07:
                    este nodo es ahora el ÚNICO lugar para todo lo de IA). */}
                {aiUsage && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 flex items-center justify-between text-xs">
                    <span className="text-purple-700 font-medium">Mensajes de IA usados hoy</span>
                    <span className="font-bold text-purple-800">{aiUsage.usedToday} / {Number(aiUsage.dailyLimit) === 0 ? 'ilimitado' : aiUsage.dailyLimit}</span>
                  </div>
                )}
                {aiUsage && !aiUsage.enabled && (
                  <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    ⚠️ La IA está desactivada para esta cuenta (la activa el administrador de la plataforma) -- este nodo no responderá hasta que se active.
                  </p>
                )}
                {/* ⭐ Contexto guardado vs. texto libre (pedido de Angelo,
                    2026-08-10). Mismo patrón que "Enviar mensaje": o apuntás a
                    un contexto con nombre (que el cliente edita desde su propia
                    sección, sin entrar acá) o escribís uno suelto que vive solo
                    en este nodo. Lo ya escrito sigue funcionando tal cual. */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Base de conocimientos</label>
                  <div className="flex gap-1.5 mt-1">
                    <button
                      onClick={() => updateNodeConfig(selectedNode.id, { aiContextId: null })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${!selectedNode.data.config.aiContextId ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      Texto libre
                    </button>
                    <button
                      onClick={() => updateNodeConfig(selectedNode.id, { aiContextId: aiContexts[0]?.id || null })}
                      disabled={aiContexts.length === 0}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-40 ${selectedNode.data.config.aiContextId ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      Contexto guardado
                    </button>
                  </div>

                  {selectedNode.data.config.aiContextId ? (
                    <>
                      <select
                        value={selectedNode.data.config.aiContextId || ''}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { aiContextId: Number(e.target.value) })}
                        className="w-full mt-1.5 px-2 py-1.5 border rounded-lg text-sm"
                      >
                        {aiContexts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <p className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded p-1.5 mt-1.5 leading-tight">
                        ✨ Este contexto se edita desde <b>Contextos IA</b> en el menú lateral. Al cambiarlo ahí, todos los
                        workflows que lo usan quedan actualizados sin abrirlos ni volver a guardar.
                      </p>
                    </>
                  ) : (
                    <>
                      <textarea
                        value={selectedNode.data.config.knowledgeBase || ''}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { knowledgeBase: e.target.value })}
                        placeholder="Ej: Sos el asistente de postventa. Si el cliente pregunta por garantía, indicá que dura 30 días..."
                        rows={6}
                        maxLength={4000}
                        className="w-full mt-1.5 px-2 py-1.5 border rounded-lg text-sm"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        {(selectedNode.data.config.knowledgeBase || '').length}/4000. Vacío = contexto genérico.
                        {aiContexts.length === 0
                          ? ' Creá un contexto en "Contextos IA" para poder reutilizarlo en varios nodos y editarlo sin entrar acá.'
                          : ' Este texto vive solo en este nodo: para cambiarlo hay que abrir el workflow.'}
                      </p>
                    </>
                  )}
                </div>
                {/* ⭐ Respuesta en audio, por nodo (pedido de Angelo,
                    2026-08-10). Antes era global: si el cliente mandaba audio,
                    SIEMPRE se contestaba en audio. Hay flujos donde conviene
                    texto (queda legible en el hilo y evita la llamada extra de
                    voz), así que ahora lo decide cada nodo. */}
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <input
                    type="checkbox"
                    checked={selectedNode.data.config.replyWithAudio !== false}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { replyWithAudio: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-semibold">Si el cliente manda un audio, responder en audio</span>
                    <span className="block text-[10px] text-gray-500 mt-0.5">
                      Destildado, este nodo contesta siempre en texto aunque le hablen por nota de voz.
                      Solo funciona con OpenAI como proveedor de la cuenta; con cualquier otro se responde en texto igual.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-indigo-50 border border-indigo-200 rounded-lg p-2">
                  <input
                    type="checkbox"
                    checked={selectedNode.data.config.autoExtractCustomFields !== false}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { autoExtractCustomFields: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-semibold">Guardar datos mencionados en campos personalizados</span>
                    <span className="block text-[10px] text-gray-500 mt-0.5">
                      Si el cliente dice, por ejemplo, “tengo 3 hijos”, la IA intentará guardar ese dato en el campo correspondiente. No inventa valores.
                    </span>
                  </span>
                </label>
                <p className="text-[10px] text-gray-400">Responde según la última conversación del contacto y descuenta del mismo tope diario mostrado arriba.</p>
                {/* ⭐ Pedido de Angelo, 2026-08-07: que la IA sepa de
                    inventario sin tener que pegar el catálogo entero acá.
                    Se implementó del lado del backend (aiReply.js) como
                    búsqueda automática por palabras clave del mensaje del
                    cliente -- no consume tokens de más porque solo agrega
                    contexto cuando encuentra productos que coinciden. */}
                <p className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 rounded-lg p-2">
                  📦 La IA ya consulta tu inventario real automáticamente cuando el cliente pregunta por un producto o precio (no hace falta escribirlo acá) -- no necesitás pegar el catálogo en esta caja.
                </p>
              </>
            )}

            {selectedNode.data.kind === 'step' && selectedNode.data.stepType === 'internal_notification' && (
              <>
                <p className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  🔔 Avisa a tu NEGOCIO por WhatsApp (no al contacto) usando el primer canal de WhatsApp activo de la cuenta -- ej. "avisar cuando entra un lead caliente".
                </p>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Números de WhatsApp fijos</label>
                  <input
                    type="text"
                    value={selectedNode.data.config.fixedNumbers || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { fixedNumbers: e.target.value })}
                    placeholder="Ej: 593987654321, 593912345678"
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Con código de país, separados por coma. Opcional si elegís empleados abajo.</p>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Empleados a notificar</label>
                  {employees.filter(e => e.phone).length === 0 ? (
                    <p className="text-[10px] text-gray-400 mt-1">Ningún empleado tiene un teléfono de WhatsApp guardado todavía (se agrega desde Empleados, al crear o editar uno).</p>
                  ) : (
                    <div className="mt-1 space-y-1 max-h-32 overflow-y-auto border rounded-lg p-1.5">
                      {employees.filter(e => e.phone).map(emp => {
                        const selected = Array.isArray(selectedNode.data.config.employeeIds) && selectedNode.data.config.employeeIds.includes(emp.id);
                        return (
                          <label key={emp.id} className="flex items-center gap-2 text-xs px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const current = Array.isArray(selectedNode.data.config.employeeIds) ? selectedNode.data.config.employeeIds : [];
                                const next = e.target.checked ? [...current, emp.id] : current.filter(id => id !== emp.id);
                                updateNodeConfig(selectedNode.id, { employeeIds: next });
                              }}
                            />
                            <span className="text-gray-700">{emp.name}</span>
                            <span className="text-gray-400">({emp.phone})</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ⭐ Tercer modo: redactada por IA (pedido de Angelo,
                    2026-08-10). El caso real: el cliente explica su problema en
                    varios mensajes y el asesor recibía "nuevo lead" sin nada
                    más, teniendo que leerse el hilo entero. */}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => updateNodeConfig(selectedNode.id, { templateId: null, useAi: false })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${!selectedNode.data.config.templateId && !selectedNode.data.config.useAi ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Texto libre
                  </button>
                  <button
                    onClick={() => updateNodeConfig(selectedNode.id, { templateId: templates[0]?.id || null, content: null, useAi: false })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${selectedNode.data.config.templateId && !selectedNode.data.config.useAi ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Plantilla
                  </button>
                  {hasAiAccess && (
                    <button
                      onClick={() => updateNodeConfig(selectedNode.id, { useAi: true, templateId: null })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${selectedNode.data.config.useAi ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      ✨ Con IA
                    </button>
                  )}
                </div>

                {selectedNode.data.config.useAi ? (
                  <>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Encabezado fijo (opcional)</label>
                      <textarea
                        value={selectedNode.data.config.aiHeader || ''}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { aiHeader: e.target.value })}
                        placeholder={'🔴 CASO DERIVADO\n{{contact.name}} · {{contact.phone}}'}
                        rows={3}
                        maxLength={400}
                        className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        Va arriba del resumen y la IA <b>no lo toca</b>. Poné acá el teléfono y el nombre: así son siempre exactos.
                        La IA tiene prohibido escribir números de teléfono, justamente para que nunca invente un dígito.
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Qué debe decir el aviso</label>
                      <textarea
                        value={selectedNode.data.config.aiPrompt || ''}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { aiPrompt: e.target.value })}
                        placeholder={'Ej: Resumí para el asesor:\n- Qué necesita el cliente\n- Qué servicio pidió\n- Urgencia (alta/media/baja)\n- Datos que ya dio (mascota, raza, síntoma)\n- Qué falta preguntar'}
                        rows={7}
                        maxLength={1500}
                        className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        {(selectedNode.data.config.aiPrompt || '').length}/1500. La IA lee los últimos <b>15 mensajes</b> de la
                        conversación (del cliente y del negocio) y escribe el aviso con lo que le pidas.
                      </p>
                    </div>

                    <p className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-2 leading-tight">
                      ✨ Descuenta del mismo tope diario de IA que las respuestas.
                      Si el proveedor falla, la notificación <b>se manda igual</b> con los últimos mensajes en crudo y un aviso —
                      nunca te quedás sin enterarte. Si hay audios del cliente sin transcribir, lo dice explícitamente.
                    </p>
                  </>
                ) : selectedNode.data.config.templateId ? (
                  templates.length === 0 ? (
                    <p className="text-xs text-gray-500">Todavía no tenés plantillas. <button onClick={onManageTemplates} className="text-blue-600 underline">Crear una</button></p>
                  ) : (
                    <select value={selectedNode.data.config.templateId || ''} onChange={(e) => updateNodeConfig(selectedNode.id, { templateId: Number(e.target.value) })} className="w-full px-2 py-1.5 border rounded-lg text-sm">
                      {templates.map(t => <option key={t.id} value={t.id}>{t.has_media ? '📎 ' : ''}{t.name}</option>)}
                    </select>
                  )
                ) : (
                  <div>
                    <div className="relative">
                      <textarea
                        ref={internalNotifContentRef}
                        value={selectedNode.data.config.content || ''}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { content: e.target.value })}
                        placeholder="Ej: 🔥 Nuevo lead caliente: {{contact.name}} ({{contact.phone}})"
                        rows={4}
                        maxLength={1000}
                        className="w-full px-2 py-1.5 pr-7 border rounded-lg text-sm"
                      />
                      <div className="absolute top-1.5 right-1.5">
                        <CustomValueButton
                          textareaRef={internalNotifContentRef}
                          value={selectedNode.data.config.content || ''}
                          onChange={(v) => updateNodeConfig(selectedNode.id, { content: v })}
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{(selectedNode.data.config.content || '').length}/1000 caracteres.</p>
                  </div>
                )}
                <p className="text-[10px] text-gray-400">Podés usar <code>{'{{contact.name}}'}</code>, <code>{'{{contact.phone}}'}</code>, etc. del contacto que disparó el workflow.</p>
                {/* En modo IA no se ofrece el envío de prueba: el texto se
                    genera a partir de una conversación real, así que no hay
                    nada que previsualizar sin un contacto de por medio. */}
                {!selectedNode.data.config.useAi && (
                  <TestSendBox
                    templateId={selectedNode.data.config.templateId}
                    content={selectedNode.data.config.content}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showRuns && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={() => setShowRuns(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-800">Historial de ejecuciones</h3>
              <button onClick={() => setShowRuns(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-[10px] text-gray-400 -mt-2 mb-2">Click en una corrida para ver su ruta de ejecución paso a paso.</p>
            <div className="flex-1 overflow-y-auto space-y-1.5">
              {runs.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">Este workflow todavía no corrió para ningún contacto.</p>
              ) : runs.map((run) => {
                const status = RUN_STATUS[run.status] || { label: run.status, className: 'bg-gray-100 text-gray-600' };
                const isOpen = expandedRunId === run.id;
                const steps = runSteps[run.id];
                return (
                  <div key={run.id} className="border rounded-lg text-sm overflow-hidden">
                    <button
                      onClick={() => toggleRunSteps(run)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{run.contact_name || run.contact_phone || 'Sin nombre'}</p>
                          <p className="text-[10px] text-gray-400">{formatEcuadorDate(run.created_at)} {formatEcuadorTime(run.created_at)}</p>
                          {run.status === 'failed' && run.last_error && <p className="text-[10px] text-red-500 truncate">{run.last_error}</p>}
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${status.className}`}>{status.label}</span>
                    </button>
                    {isOpen && (
                      <div className="border-t bg-gray-50 px-3 py-2.5">
                        {loadingRunSteps && !steps ? (
                          <p className="text-[11px] text-gray-400 text-center py-2">Cargando ruta...</p>
                        ) : !steps || steps.length === 0 ? (
                          <p className="text-[11px] text-gray-400 text-center py-2">Esta corrida todavía no ejecutó ningún paso (¿sigue en espera?).</p>
                        ) : (
                          // ⭐ La "ruta de trabajo" en sí: una línea de tiempo vertical,
                          // un ítem por paso REALMENTE ejecutado, en el orden en que
                          // pasó -- exactamente lo que Angelo pidió replicar de GHL
                          // (click en una acción del historial → ver por dónde se hizo).
                          <ol className="space-y-2.5 relative before:absolute before:left-[9px] before:top-1 before:bottom-1 before:w-px before:bg-gray-200">
                            {steps.map((rs) => {
                              const meta = STEP_META[rs.step_type] || { label: rs.step_type, icon: Zap };
                              const Icon = meta.icon;
                              const detail = safeParse(rs.detail);
                              const failed = rs.status === 'failed';
                              return (
                                <li key={rs.id} className="flex items-start gap-2 relative pl-0.5">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${failed ? 'bg-red-100 text-red-600' : 'bg-white border-2 border-blue-400 text-blue-600'}`}>
                                    {failed ? <AlertCircle className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                                  </span>
                                  <div className="min-w-0 flex-1 pb-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-xs font-semibold text-gray-800">{meta.label}</p>
                                      <span className="text-[9px] text-gray-400">{formatEcuadorTime(rs.executed_at)}</span>
                                    </div>
                                    {failed ? (
                                      <p className="text-[11px] text-red-600">{detail.error || 'Falló sin detalle.'}</p>
                                    ) : (
                                      <p className="text-[11px] text-gray-500">{describeRunStepDetail(rs.step_type, detail, { pipelineStages, tags }) || 'Ejecutado'}</p>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                            {run.status === 'completed' && (() => {
                              // Solo el paso "Fin / Meta" es un final legítimo. Si la corrida
                              // terminó en cualquier otro paso es porque ese paso no tenía
                              // ningún paso siguiente conectado -- antes esto se veía igual
                              // que un final correcto y no había forma de notarlo.
                              const last = steps[steps.length - 1];
                              const okEnd = !last || last.step_type === 'end_goal';
                              if (okEnd) {
                                return (
                                  <li className="flex items-center gap-2 relative pl-0.5">
                                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-green-100 text-green-600"><CheckCircle2 className="w-3 h-3" /></span>
                                    <p className="text-xs font-semibold text-green-700">Workflow completado</p>
                                  </li>
                                );
                              }
                              const lastLabel = (STEP_META[last.step_type] || {}).label || last.step_type;
                              return (
                                <li className="flex items-start gap-2 relative pl-0.5">
                                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-amber-100 text-amber-600"><AlertCircle className="w-3 h-3" /></span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-amber-700">Terminó acá, sin llegar a un "Fin / Meta"</p>
                                    <p className="text-[11px] text-amber-600">
                                      El paso "{lastLabel}" no tiene ningún paso siguiente conectado, así que el flujo se detuvo ahí.
                                    </p>
                                  </div>
                                </li>
                              );
                            })()}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ⭐ Gestor de plantillas de mensaje -- modal simple de CRUD, reusado tanto
// desde acá (botón en la lista) como desde el panel de configuración del
// paso "Enviar mensaje" (onManageTemplates).
// ⭐ `embedded` (2026-08-09, pedido de Angelo: "que las plantillas tengan su
// propio espacio en el sidebar"). El MISMO componente sirve para el modal que
// se abre desde Workflows y para la pantalla completa del menú lateral: en
// modo embebido se saca el overlay oscuro, el recorte de alto y el botón de
// cerrar, y nada más. Duplicarlo en dos componentes habría significado
// mantener dos veces la lógica de adjuntos y valores personalizados, que es
// justo donde se desincronizan.
const TemplatesModal = ({ templates, onClose, onChanged, embedded = false }) => {
  const [editing, setEditing] = useState(null); // {id, name, content} | 'new' | null
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const contentRef = useRef(null);
  const [customAlert, setCustomAlert] = useState(null);
  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  // ⭐ Adjuntar imagen/audio/video/documento a una plantilla (pedido de
  // Angelo, 2026-08-07). `fileAttachment` es un archivo NUEVO elegido en
  // esta sesión de edición (ver buildAttachmentFromFile en
  // utils/mediaAttachment.js, trae el base64 listo para mandar).
  // `existingMedia` es el adjunto que la plantilla YA tenía guardado (solo
  // metadata -- nunca se baja el base64 acá, ver GET
  // .../message-templates/:id/media si se necesitara previsualizar). Si el
  // usuario elige un archivo nuevo, reemplaza a `existingMedia` en la UI;
  // si hace click en "quitar", se marca `removeMedia` para que el PUT borre
  // el adjunto guardado sin tener que resubir nada.
  const [fileAttachment, setFileAttachment] = useState(null);
  const [existingMedia, setExistingMedia] = useState(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const [attachError, setAttachError] = useState('');
  const fileInputRef = useRef(null);

  const resetAttachmentState = () => {
    if (fileAttachment?.localPreviewUrl) URL.revokeObjectURL(fileAttachment.localPreviewUrl);
    setFileAttachment(null);
    setExistingMedia(null);
    setRemoveMedia(false);
    setAttachError('');
  };

  const startNew = () => { setEditing('new'); setName(''); setContent(''); resetAttachmentState(); };
  const startEdit = (tpl) => {
    setEditing(tpl.id);
    setName(tpl.name);
    setContent(tpl.content);
    setFileAttachment(null);
    setRemoveMedia(false);
    setAttachError('');
    setExistingMedia(tpl.has_media ? { mimetype: tpl.media_mimetype, fileName: tpl.media_filename, kind: tpl.media_kind } : null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite elegir el mismo archivo dos veces seguidas
    if (!file) return;
    try {
      const attachment = await buildAttachmentFromFile(file);
      setFileAttachment(attachment);
      setExistingMedia(null);
      setRemoveMedia(false);
      setAttachError('');
    } catch (err) {
      setAttachError(err.message || 'No se pudo leer el archivo.');
    }
  };

  const removeAttachment = () => {
    if (fileAttachment?.localPreviewUrl) URL.revokeObjectURL(fileAttachment.localPreviewUrl);
    setFileAttachment(null);
    setExistingMedia(null);
    setRemoveMedia(true);
    setAttachError('');
  };

  const save = async () => {
    if (!name.trim() || !content.trim()) return;
    const payload = { name: name.trim(), content: content.trim() };
    if (fileAttachment) {
      payload.mediaBase64 = fileAttachment.base64;
      payload.mediaMimetype = fileAttachment.mimetype;
      payload.mediaFilename = fileAttachment.fileName;
      payload.mediaKind = fileAttachment.kind;
    } else if (removeMedia) {
      payload.removeMedia = true;
    }
    try {
      if (editing === 'new') {
        await apiClient.post('/api/pos/crm/message-templates', payload);
      } else {
        await apiClient.put(`/api/pos/crm/message-templates/${editing}`, payload);
      }
      setEditing(null);
      resetAttachmentState();
      onChanged();
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo guardar la plantilla.');
    }
  };

  const remove = (tpl) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Eliminar plantilla',
      message: `¿Eliminar la plantilla "${tpl.name}"? Los workflows que la usan quedarán sin mensaje configurado en ese paso.`,
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/pos/crm/message-templates/${tpl.id}`);
          onChanged();
        } catch (err) {
          showAlert('error', 'Error', 'No se pudo eliminar la plantilla.');
        }
      }
    });
  };

  return (
    <div
      className={embedded ? 'p-6' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4'}
      onClick={embedded ? undefined : onClose}
    >
      {customAlert && (
        <CustomAlert type={customAlert.type} title={customAlert.title} message={customAlert.message} onClose={() => setCustomAlert(null)} onConfirm={customAlert.onConfirm} zIndex={80} />
      )}
      <div
        className={embedded
          ? 'bg-white rounded-xl border max-w-2xl w-full flex flex-col p-5'
          : 'bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col p-5'}
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FileTextIcon className="w-5 h-5 text-blue-600" /> Plantillas de mensaje</h3>
          {!embedded && <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>}
        </div>
        {embedded && (
          <p className="text-sm text-gray-500 -mt-2 mb-3">
            Mensajes que reutilizas desde los workflows y desde Conversaciones. Aceptan valores
            personalizados como <code className="bg-gray-100 px-1 rounded">{'{{contact.name}}'}</code> y
            pueden llevar una imagen, audio, video o documento adjunto.
          </p>
        )}

        {editing ? (
          <div className="space-y-2 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de la plantilla" className="w-full px-2.5 py-1.5 border rounded-lg text-sm" />
            <div className="relative">
              <textarea
                ref={contentRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Hola {{contact.name}}, ..."
                rows={4}
                className="w-full px-2.5 py-1.5 pr-7 border rounded-lg text-sm"
              />
              <div className="absolute top-1.5 right-1.5">
                <CustomValueButton textareaRef={contentRef} value={content} onChange={setContent} />
              </div>
            </div>
            <p className="text-[10px] text-gray-400">Podés usar <code>{'{{contact.name}}'}</code>, <code>{'{{contact.phone}}'}</code>, etc. -- botón <Braces className="w-2.5 h-2.5 inline" /> para ver todos.</p>
            {/* ⭐ Adjuntar imagen/audio/video/documento (pedido de Angelo,
                2026-08-07): un solo archivo por plantilla, se manda como
                caption el texto de arriba (ver executeSendMessage en
                workflowEngine.js y POST .../conversations/:id/messages). */}
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} className="hidden" onChange={handleFileChange} />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 px-2 py-1 border rounded-lg text-[11px] text-gray-500 hover:bg-gray-50"
              >
                <Paperclip className="w-3 h-3" /> Adjuntar imagen/audio/video
              </button>
              {(fileAttachment || existingMedia) && (
                <span className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-700">
                  📎 {fileAttachment?.kind || existingMedia?.kind}
                  {(fileAttachment?.fileName || existingMedia?.fileName) ? ` · ${fileAttachment?.fileName || existingMedia?.fileName}` : ''}
                  <button type="button" onClick={removeAttachment} title="Quitar adjunto" className="text-blue-400 hover:text-blue-700">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
            {attachError && <p className="text-[10px] text-red-500">{attachError}</p>}
            <div className="flex gap-1.5">
              <button onClick={save} className="flex-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">Guardar</button>
              <button onClick={() => { setEditing(null); resetAttachmentState(); }} className="flex-1 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200">Cancelar</button>
            </div>
          </div>
        ) : (
          <button onClick={startNew} className="w-full py-2 mb-3 border-2 border-dashed rounded-lg text-xs font-semibold text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Nueva plantilla
          </button>
        )}

        <div className="flex-1 overflow-y-auto space-y-1.5">
          {templates.length === 0 && !editing ? (
            <p className="text-center text-gray-400 text-sm py-6">Todavía no tenés plantillas.</p>
          ) : templates.map((tpl) => (
            <div key={tpl.id} className="border rounded-lg px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm text-gray-800 truncate flex items-center gap-1">
                  {tpl.name}
                  {tpl.has_media && <Paperclip className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                </p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(tpl)} className="text-gray-400 hover:text-blue-600 text-xs font-semibold">Editar</button>
                  <button onClick={() => remove(tpl)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <p className="text-xs text-gray-500 truncate mt-0.5">{tpl.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const isOwner = sessionStorage.getItem('userType') === 'cliente';

const WorkflowsManager = () => {
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [tags, setTags] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Contextos de IA reutilizables (ver ContextosIAManager.js) -- el nodo de IA
  // elige uno en vez de traer el texto adentro.
  const [aiContexts, setAiContexts] = useState([]);
  const [view, setView] = useState('list');
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [newWfName, setNewWfName] = useState('');
  const [newWfTrigger, setNewWfTrigger] = useState('stage_changed');
  const [customAlert, setCustomAlert] = useState(null);
  const loadFolders = useCallback(() => {
    apiClient.get('/api/pos/crm/workflow-folders').then(res => setFolders(res.data || [])).catch(() => {});
  }, []);
  // ⭐ Habilita/oculta el nodo 'Responder con IA' en el constructor (pedido
  // de Angelo, 2026-08-06). GET /api/pos/crm/ai-settings es owner-only
  // (ver backend/index.js) -- solo se pide si isOwner, para no generar un
  // 403 de ruido en la consola cuando entra un empleado con permiso de CRM.
  const [hasAiAccess, setHasAiAccess] = useState(false);
  // ⭐ Lista de empleados con teléfono (pedido de Angelo, 2026-08-07): para
  // poder elegirlos como destinatario del nodo 'Notificación interna
  // (WhatsApp)'. GET /api/pos/employees es owner-only (igual que
  // ai-settings arriba) -- mismo criterio: solo se pide si isOwner, para no
  // generar un 403 de ruido cuando entra un empleado con permiso de CRM. Un
  // empleado editando un workflow simplemente no ve el selector de
  // empleados en ese nodo (sigue pudiendo usar números fijos).
  const [employees, setEmployees] = useState([]);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  useEffect(() => {
    apiClient.get('/api/pos/crm/access')
      .then(res => setHasAccess(Boolean(res.data?.hasCrmAccess)))
      .catch(() => setHasAccess(false))
      .finally(() => setLoadingAccess(false));
    if (isOwner) {
      apiClient.get('/api/pos/crm/ai-settings')
        .then(res => setHasAiAccess(Boolean(res.data?.enabled)))
        .catch(() => setHasAiAccess(false));
      apiClient.get('/api/pos/employees')
        .then(res => setEmployees(res.data || []))
        .catch(() => setEmployees([]));
    }
  }, []);

  const loadReferenceData = useCallback(() => {
    Promise.all([
      apiClient.get('/api/pos/crm/tags'),
      apiClient.get('/api/pos/crm/pipeline-stages'),
      apiClient.get('/api/pos/crm/pipelines'),
      apiClient.get('/api/pos/crm/message-templates'),
      apiClient.get('/api/pos/crm/ai-contexts'),
    ]).then(([tagsRes, stagesRes, pipelinesRes, tplRes, ctxRes]) => {
      setTags(tagsRes.data || []);
      setPipelineStages(stagesRes.data || []);
      setPipelines(pipelinesRes.data || []);
      setTemplates(tplRes.data || []);
      setAiContexts(ctxRes.data || []);
    }).catch(() => {});
  }, []);

  const loadWorkflows = useCallback(() => {
    apiClient.get('/api/pos/crm/workflows').then(res => setWorkflows(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasAccess) return;
    loadReferenceData();
    loadWorkflows();
    loadFolders();
  }, [hasAccess, loadReferenceData, loadWorkflows, loadFolders]);

  const createFolder = async () => {
    const name = window.prompt('Nombre de la carpeta');
    if (!name?.trim()) return;
    try { await apiClient.post('/api/pos/crm/workflow-folders', { name: name.trim() }); loadFolders(); }
    catch (err) { showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear la carpeta.'); }
  };

  const createWorkflow = async () => {
    if (!newWfName.trim()) return;
    try {
      const res = await apiClient.post('/api/pos/crm/workflows', { name: newWfName.trim(), trigger_type: newWfTrigger, trigger_config: {}, folder_id: selectedFolderId });
      setShowCreateModal(false);
      setNewWfName('');
      setNewWfTrigger('stage_changed');
      loadWorkflows();
      setEditingWorkflowId(res.data.id);
      setView('editor');
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear el workflow.');
    }
  };

  const toggleActive = async (wf) => {
    try {
      await apiClient.put(`/api/pos/crm/workflows/${wf.id}`, { is_active: !wf.is_active });
      loadWorkflows();
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo cambiar el estado (¿tiene al menos un paso?).');
    }
  };

  const moveWorkflowToFolder = async (wf, folderId) => {
    try {
      await apiClient.put(`/api/pos/crm/workflows/${wf.id}`, { folder_id: folderId ? Number(folderId) : null });
      loadWorkflows();
    } catch (err) { showAlert('error', 'Error', 'No se pudo clasificar el workflow.'); }
  };

  const deleteWorkflow = (wf) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Eliminar workflow',
      message: `¿Eliminar "${wf.name}"? Se borra el flujo completo y su historial de ejecuciones. Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/pos/crm/workflows/${wf.id}`);
          loadWorkflows();
        } catch (err) {
          showAlert('error', 'Error', 'No se pudo eliminar el workflow.');
        }
      }
    });
  };

  if (loadingAccess) return <div className="p-8 text-center text-gray-500">Cargando...</div>;

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Lock className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Workflows no está incluido en tu plan</h2>
        <p className="text-gray-500 max-w-md">Este módulo viene con Conversaciones -- contacta a soporte para activarlo.</p>
      </div>
    );
  }

  if (view === 'editor' && editingWorkflowId) {
    return (
      <div className="h-full flex flex-col">
        {showTemplatesModal && <TemplatesModal templates={templates} onClose={() => setShowTemplatesModal(false)} onChanged={loadReferenceData} />}
        <WorkflowEditor
          workflowId={editingWorkflowId}
          tags={tags}
          allWorkflows={workflows}
          pipelineStages={pipelineStages}
          pipelines={pipelines}
          templates={templates}
          aiContexts={aiContexts}
          hasAiAccess={hasAiAccess}
          employees={employees}
          onBack={() => { setView('list'); setEditingWorkflowId(null); loadWorkflows(); }}
          onManageTemplates={() => setShowTemplatesModal(true)}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {customAlert && (
        <CustomAlert type={customAlert.type} title={customAlert.title} message={customAlert.message} onClose={() => setCustomAlert(null)} onConfirm={customAlert.onConfirm} zIndex={80} />
      )}
      {showTemplatesModal && <TemplatesModal templates={templates} onClose={() => setShowTemplatesModal(false)} onChanged={loadReferenceData} />}

      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <WorkflowIcon className="w-5 h-5 text-blue-600" /> Workflows
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTemplatesModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">
            <FileTextIcon className="w-4 h-4" /> Plantillas
          </button>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Nuevo workflow
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => setSelectedFolderId(null)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${selectedFolderId == null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>Todos</button>
          {folders.map(folder => <button key={folder.id} onClick={() => setSelectedFolderId(folder.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${selectedFolderId === folder.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>📁 {folder.name}</button>)}
          <button onClick={createFolder} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-dashed text-gray-500 hover:text-blue-600">+ Carpeta</button>
        </div>
        {(workflows.filter(wf => selectedFolderId == null || Number(wf.folder_id) === Number(selectedFolderId))).length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <WorkflowIcon className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-bold text-gray-700 mb-2">Todavía no tienes ningún workflow</h2>
            <p className="text-gray-500 max-w-md">Automatiza acciones como mandar un mensaje cuando un contacto cambia de etapa o se le agrega una etiqueta.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {workflows.filter(wf => selectedFolderId == null || Number(wf.folder_id) === Number(selectedFolderId)).map((wf) => (
              <div key={wf.id} className="border rounded-xl p-4 bg-white hover:shadow-md transition">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => { setEditingWorkflowId(wf.id); setView('editor'); }} className="text-left min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{wf.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{TRIGGER_LABELS[wf.trigger_type]}</p>
                    {wf.folder_name && <p className="text-[10px] text-blue-500 mt-1">📁 {wf.folder_name}</p>}
                  </button>
                  <button
                    onClick={() => toggleActive(wf)}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${wf.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {wf.is_active ? 'Activo' : 'Pausado'}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3 text-[10px] text-gray-400">
                  <span>{wf.step_count} paso{wf.step_count === 1 ? '' : 's'}</span>
                  <select value={wf.folder_id || ''} onChange={(e) => moveWorkflowToFolder(wf, e.target.value)} onClick={(e) => e.stopPropagation()} className="max-w-28 border rounded px-1 py-0.5 text-[10px] text-gray-500">
                    <option value="">Sin carpeta</option>{folders.map(folder => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
                  </select>
                  {wf.active_run_count > 0 && <span className="text-blue-500 font-semibold">{wf.active_run_count} en curso</span>}
                  {isOwner && (
                    <button onClick={() => deleteWorkflow(wf)} className="text-gray-300 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Nuevo workflow</h3>
              <button onClick={() => setShowCreateModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <input
              type="text"
              placeholder="Nombre (ej. Bienvenida a nuevos leads)"
              value={newWfName}
              onChange={(e) => setNewWfName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
            />
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Disparador</label>
            <select value={newWfTrigger} onChange={(e) => setNewWfTrigger(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm mb-4">
              {Object.entries(TRIGGER_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <p className="text-xs text-gray-500 mb-4">Después de crearlo podés afinar el disparador (ej. a qué etapa exacta) y armar los pasos en el editor visual.</p>
            <button onClick={createWorkflow} className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
              Crear y continuar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowsManager;
// ⭐ Se exporta para PlantillasManager.js (la sección propia del sidebar), que
// lo monta en modo `embedded`. Ver la nota junto a la definición.
export { TemplatesModal };
